import React, { useState, useEffect, useRef } from 'react';
import { 
  LogOut, Wallet, ShoppingBag, User, 
  AlertCircle, CheckCircle, Clock, Key, Download, ArrowRight, 
  Phone, MessageSquare, Video, Smartphone, ShieldCheck, PlusCircle,
  Search, Plus, Minus, RefreshCw
} from 'lucide-react';
import { io } from 'socket.io-client'; 

const API_URL = 'https://society-backend-dpj5.onrender.com/api';
const socket = io('https://society-backend-dpj5.onrender.com', {
  transports: ['websocket', 'polling']
});

socket.on('connect', () => {
  console.log('🟢 Socket.IO Connected:', socket.id);
});

socket.on('connect_error', (err) => {
  console.error('🔴 Socket.IO Connection Error:', err.message);
});

const ParticlesBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let particlesArray;
    let animationFrameId;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    let mouse = { x: null, y: null, radius: 150 };

    window.addEventListener('mousemove', (event) => {
      mouse.x = event.x;
      mouse.y = event.y;
    });

    window.addEventListener('resize', () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      init();
    });

    class Particle {
      constructor(x, y, directionX, directionY, size) {
        this.x = x;
        this.y = y;
        this.directionX = directionX;
        this.directionY = directionY;
        this.size = size;
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2, false);
        ctx.fillStyle = 'rgba(212, 175, 55, 0.6)';
        ctx.fill();
      }
      update() {
        if (this.x > canvas.width || this.x < 0) this.directionX = -this.directionX;
        if (this.y > canvas.height || this.y < 0) this.directionY = -this.directionY;
        this.x += this.directionX;
        this.y += this.directionY;
        this.draw();
      }
    }

    function init() {
      particlesArray = [];
      let numberOfParticles = (canvas.height * canvas.width) / 10000;
      for (let i = 0; i < numberOfParticles; i++) {
        let size = (Math.random() * 2) + 1;
        let x = (Math.random() * ((window.innerWidth - size * 2) - (size * 2)) + size * 2);
        let y = (Math.random() * ((window.innerHeight - size * 2) - (size * 2)) + size * 2);
        let directionX = (Math.random() * 0.4) - 0.2; 
        let directionY = (Math.random() * 0.4) - 0.2;
        particlesArray.push(new Particle(x, y, directionX, directionY, size));
      }
    }

    function animate() {
      animationFrameId = requestAnimationFrame(animate);
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (let i = 0; i < particlesArray.length; i++) {
        particlesArray[i].update();
      }
      connect();
    }

    function connect() {
      for (let a = 0; a < particlesArray.length; a++) {
        if (mouse.x != null) {
          let dxMouse = particlesArray[a].x - mouse.x;
          let dyMouse = particlesArray[a].y - mouse.y;
          let distanceMouse = Math.sqrt(dxMouse * dxMouse + dyMouse * dyMouse);
          if (distanceMouse < mouse.radius) {
            ctx.strokeStyle = `rgba(212, 175, 55, ${1 - distanceMouse / mouse.radius})`;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(particlesArray[a].x, particlesArray[a].y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();
          }
        }
        for (let b = a; b < particlesArray.length; b++) {
          let distance = ((particlesArray[a].x - particlesArray[b].x) * (particlesArray[a].x - particlesArray[b].x)) + 
                         ((particlesArray[a].y - particlesArray[b].y) * (particlesArray[a].y - particlesArray[b].y));
          if (distance < (canvas.width / 10) * (canvas.height / 10)) {
            let opacityValue = 1 - (distance / 15000);
            ctx.strokeStyle = `rgba(212, 175, 55, ${opacityValue * 0.3})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particlesArray[a].x, particlesArray[a].y);
            ctx.lineTo(particlesArray[b].x, particlesArray[b].y);
            ctx.stroke();
          }
        }
      }
    }

    init();
    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', init);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full z-[-1] pointer-events-none" style={{background: 'linear-gradient(to bottom, #050508, #0a0a0f)'}} />;
};

// ⏱️ ระบบกล่องนับเวลาถอยหลัง (หลอดเวลาลดลงแบบ Real-time)
const RentalTimer = ({ startAt, expiresAt }) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (!startAt || !expiresAt) return null;

  const start = new Date(startAt).getTime();
  const end = new Date(expiresAt).getTime();
  const current = now.getTime();

  // กรณีหมดเวลาแล้ว
  if (current >= end) {
    return (
      <div className="mt-4 bg-black/40 p-4 rounded-xl border border-red-500/30 fade-in">
         <div className="flex justify-between items-center text-xs font-bold text-red-500 mb-2">
           <span className="flex items-center gap-1"><AlertCircle size={14}/> หมดเวลาเช่าแล้ว</span>
           <span>0%</span>
         </div>
         <div className="w-full bg-gray-900 rounded-full h-2">
           <div className="bg-red-500 h-2 rounded-full w-0"></div>
         </div>
      </div>
    );
  }

  // คำนวณเปอร์เซ็นต์ (เปอร์เซ็นต์ที่เหลืออยู่)
  const totalDuration = end - start;
  const remaining = end - current;
  const rawProgress = (remaining / totalDuration) * 100;
  const progress = Math.min(Math.max(rawProgress, 0), 100);

  // คำนวณวัน ชม. นาที วิ
  const d = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const h = Math.floor((remaining / (1000 * 60 * 60)) % 24);
  const m = Math.floor((remaining / 1000 / 60) % 60);
  const s = Math.floor((remaining / 1000) % 60);

  let timeString = '';
  if (d > 0) timeString += `${d} วัน `;
  timeString += `${h} ชม. ${m} นาที ${s} วิ`;

  // เปลี่ยนสีหลอดตามเวลาที่เหลือ (เหลือน้อยให้แดง)
  const barColor = progress < 10 ? 'bg-red-500' : progress < 30 ? 'bg-orange-500' : 'bg-green-500';

  return (
    <div className="mt-4 bg-black/60 p-4 rounded-xl border border-white/5 shadow-inner fade-in">
      <div className="flex justify-between items-center mb-3">
        <span className="text-xs font-bold text-gray-400 flex items-center gap-1">
          <Clock size={14} className="text-yellow-400 animate-pulse"/> เวลาคงเหลือ
        </span>
        <span className="text-[13px] font-black text-white eng-num bg-black/50 px-3 py-1.5 rounded-lg border border-white/10 shadow-sm">
          {timeString}
        </span>
      </div>
      <div className="w-full bg-gray-900 rounded-full h-2 overflow-hidden border border-white/5">
         <div 
           className={`${barColor} h-full rounded-full transition-all duration-1000 ease-linear shadow-[0_0_10px_currentColor] opacity-90`} 
           style={{ width: `${progress}%` }}
         ></div>
      </div>
    </div>
  );
};

export default function SOCIETYxSHOP() {
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [steamCodes, setSteamCodes] = useState({});
  const [fetchingCode, setFetchingCode] = useState({});
  const [guardCooldowns, setGuardCooldowns] = useState({});
  const [showRentalModal, setShowRentalModal] = useState(null);
  const [rentalRates, setRentalRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);

// ⏱️ ระบบนับเวลาถอยหลังปุ่มขอ Steam Guard (ลดลงทีละ 1 วินาที)
useEffect(() => {
  const timer = setInterval(() => {
    setGuardCooldowns((prev) => {
      const updatedCooldowns = { ...prev };
      let hasChanges = false;

      Object.keys(updatedCooldowns).forEach((orderId) => {
        if (updatedCooldowns[orderId] > 0) {
          updatedCooldowns[orderId] -= 1;
          hasChanges = true;
        } else {
          delete updatedCooldowns[orderId]; // ถ้าเวลาหมดแล้ว ให้ลบทิ้ง
          hasChanges = true;
        }
      });

      return hasChanges ? updatedCooldowns : prev;
    });
  }, 1000);

  return () => clearInterval(timer);
}, []);

  useEffect(() => {
    const acceptedTerms = localStorage.getItem('accepted_terms');
    if (!acceptedTerms) {
      setShowTermsModal(true);
    }
  }, []);

  const [currentPage, setCurrentPage] = useState('landing'); 
  const [user, setUser] = useState(null);
  const [isGuest, setIsGuest] = useState(false); 
  const [showBuyModal, setShowBuyModal] = useState(null); 
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [recentPurchase, setRecentPurchase] = useState(null);

  const [adminStats, setAdminStats] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]); 
  const [userSearch, setUserSearch] = useState("");
  const [bulkKeys, setBulkKeys] = useState("");
  const [selectedAdminProduct, setSelectedAdminProduct] = useState("CMD SOCIETY");

  const [authMode, setAuthMode] = useState('login');
  const [formData, setFormData] = useState({ username: '', email: '', password: '' });
  const [passForm, setPassForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });

  const [forgotEmail, setForgotEmail] = useState('');
  const [resetData, setResetData] = useState({ otp: '', newPassword: '' });

  const [refillAmount, setRefillAmount] = useState('');
  const [qrImage, setQrImage] = useState(null);
  const [expectedAmount, setExpectedAmount] = useState(0);

  const [slipImage, setSlipImage] = useState(null);
  const [slipPreview, setSlipPreview] = useState(null);
  const [refillHistory, setRefillHistory] = useState([]);

  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('ทั้งหมด');
  const [announcements, setAnnouncements] = useState([]);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [searchTerm, setSearchTerm] = useState(''); // 🔥 เพิ่ม State สำหรับเก็บคำค้นหา
  
  const STORE_LINKS = {
    discord: "https://discord.gg/TApFSKWtYK",
    tiktok: "https://www.tiktok.com/@societyxshopv1?is_from_webapp=1&sender_device=pc",
    youtube: "https://www.youtube.com/channel/UCr60L8rgOnVKaGTil_NPMgw",
    external: "https://www.mediafire.com/file/tjhm8q83phd98yg/SOCIETYWEB.rar/file",
    reshade: "https://www.mediafire.com/file/rnj6giipr99ji6y/%25E0%25B8%25A3%25E0%25B8%25B5%25E0%25B9%2580%25E0%25B8%258A%25E0%25B8%2594_Society.rar/file",
    cmd: "https://www.mediafire.com/file/tf8mshzaz0j6a9m/CMD+society.rar/file",
    systempf: "https://www.mediafire.com/file/g6caw7kn5j4fy4f/OPTIMIZATION_-_THE_ULTIMATE_CONFIG.bat/file"
  };

  useEffect(() => {
    loadProducts();
    checkAuth();
    loadAnnouncements();

    socket.on('productSold', (data) => {
      setProducts((prevProducts) =>
        prevProducts.map((product) =>
          product._id === data.productId
            ? { ...product, soldCount: data.newSoldCount }
            : product
        )
      );
      setRecentPurchase(data);
    });

    return () => {
      socket.off('productSold');
    };
  }, []);

  useEffect(() => {
    if (recentPurchase) {
      const timer = setTimeout(() => {
        setRecentPurchase(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [recentPurchase]); 

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const res = await fetch(`${API_URL}/auth/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
          setIsGuest(false);
          setCurrentPage('shop'); 
        }
      } catch (err) {
        console.log('Auth check failed');
      }
    }
  };

// 🔥 ฟังก์ชันสำหรับกดปักหมุด / ยกเลิกปักหมุดสินค้า
  const handleTogglePin = async (productId, productName) => {
    try {
      const res = await fetch(`${API_URL}/admin/products/${productId}/pin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification(`✓ อัปเดตสถานะปักหมุดของ ${productName} สำเร็จ!`, 'success');
        loadProducts(); // โหลดรายการสินค้าใหม่เพื่อให้หน้าเว็บอัปเดตทันที
      } else {
        showNotification(data.message || 'ปักหมุดไม่สำเร็จ', 'error');
      }
    } catch (err) {
      showNotification('เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error');
    }
  };

  const loadAnnouncements = async () => {
    try {
      const res = await fetch(`${API_URL}/announcements`);
      if (res.ok) {
        const data = await res.json();
        setAnnouncements(data);
      }
    } catch (err) {}
  };

  const handleAcceptTerms = () => {
    localStorage.setItem('accepted_terms', 'true');
    setShowTermsModal(false);
    showNotification('✓ ยอมรับเงื่อนไขการให้บริการเรียบร้อยแล้ว', 'success');
  };

  const loadProducts = async () => {
    try {
      const res = await fetch(`${API_URL}/products`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (err) {}
  };

  const loadOrders = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/orders`, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      if (res.ok) setOrders(await res.json());
    } catch (err) {}
  };

  const loadRefillHistory = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/refill/history`, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      if (res.ok) setRefillHistory(await res.json());
    } catch (err) {}
  };

  const loadAdminData = async () => {
    const token = localStorage.getItem('token');
    try {
      const resStats = await fetch(`${API_URL}/admin/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (resStats.ok) setAdminStats(await resStats.json());

      const resUsers = await fetch(`${API_URL}/admin/users`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (resUsers.ok) setAdminUsers(await resUsers.json());
    } catch (err) {}
  };

  const handleUpdateBalance = async (userId, username, tag, currentBalance, action) => {
    const actionText = action === 'add' ? 'เพิ่มเงินให้' : 'หักเงินจาก';
    const amount = prompt(`ต้องการ ${actionText} ${username}#${tag}\nยอดปัจจุบัน: ฿${currentBalance}\n\nระบุจำนวนเงินที่ต้องการ (บาท):`);
    
    if (!amount || isNaN(amount) || amount <= 0) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/users/balance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ userId, amount, action })
      });
      const data = await res.json();
      if (res.ok) {
        showNotification(data.message, 'success');
        loadAdminData();
      } else {
        showNotification(data.message, 'error');
      }
    } catch (err) { showNotification('เกิดข้อผิดพลาดในการปรับยอดเงิน', 'error'); }
    setLoading(false);
  };

const handleGetSteamGuard = async (orderId) => {
    setFetchingCode(prev => ({ ...prev, [orderId]: true }));
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}/steam-guard`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSteamCodes(prev => ({ ...prev, [orderId]: data.code }));
        setGuardCooldowns(prev => ({ ...prev, [orderId]: 60 }));
        showNotification('ดึงโค้ด Steam Guard สำเร็จ!', 'success');
      } else {
        showNotification(data.message || 'ขอโค้ดไม่สำเร็จ', 'error');
      }
    } catch (err) {
      showNotification('เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error');
    }
    setFetchingCode(prev => ({ ...prev, [orderId]: false }));
  };

  const handleBuy = async (product) => {
    if (isGuest) {
      showNotification('⚠ กรุณาเข้าสู่ระบบเพื่อสั่งซื้อสินค้า', 'warning');
      setAuthMode('login');
      setCurrentPage('auth');
      return;
    }
    
    // 🔥 ถ้าระบบตรวจเจอว่าเป็น "ไอดีเช่า" ให้ไปดึงราคาเช่าก่อน
    if (product.badge === 'ไอดีเช่า') {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/products/${product._id}/rental-rates`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setRentalRates(data.rates);
          setSelectedRate(data.rates[0]); // เลือกเรทแรกสุดให้เป็นค่าเริ่มต้น
          setShowRentalModal(product);
        } else {
          showNotification(data.message || 'ดึงราคาเช่าไม่สำเร็จ', 'error');
        }
      } catch (err) {
        showNotification('เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error');
      }
      setLoading(false);
      return;
    }

    if (user.balance < product.price) {
      showNotification('⚠ ยอดเงินไม่พอ กรุณาเติมเงิน', 'warning');
      return;
    }
    setShowBuyModal(product); 
  };

  const handleGenerateQR = async () => {
    if (!refillAmount || isNaN(refillAmount) || Number(refillAmount) < 1) {
      return showNotification("⚠ กรุณากรอกจำนวนเงินให้ถูกต้อง", "warning");
    }
    setLoading(true); 
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/refill/generate-qr`, { 
        method: 'POST', 
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        }, 
        body: JSON.stringify({ amount: refillAmount }) 
      });
      const data = await response.json();
      if (data.success) { 
        setQrImage(data.qrImage); 
        setExpectedAmount(data.amount); 
      } else {
        showNotification(data.message, 'error');
      }
    } catch (error) { 
      showNotification("❌ เกิดข้อผิดพลาด", 'error'); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleSlipUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSlipImage(file);
      const reader = new FileReader();
      reader.onload = (evt) => setSlipPreview(evt.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRedeemPromoCode = async () => {
    if (!promoCodeInput.trim()) return showNotification('⚠ กรุณากรอกโค้ดส่วนลดก่อน', 'warning');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/refill/redeem-code`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ code: promoCodeInput })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setUser({ ...user, balance: data.newBalance });
        showNotification(`✓ ${data.message}`, 'success');
        setPromoCodeInput('');
      } else {
        showNotification(data.message || 'ใช้โค้ดไม่สำเร็จ', 'error');
      }
    } catch (err) {
      showNotification('เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error');
    }
    setLoading(false);
  };

  const submitSlip = async () => {
    if (!slipImage) return showNotification('⚠ กรุณาเลือกรูปสลิปก่อน', 'warning');
    
    const formData = new FormData(); 
    formData.append('slip', slipImage); 
    formData.append('amount', expectedAmount); 
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/refill/verify`, { 
        method: 'POST', 
        headers: { 'Authorization': `Bearer ${token}` }, 
        body: formData 
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setUser({ ...user, balance: data.newBalance }); 
        showNotification(`✓ ${data.message}`, 'success');
        setSlipImage(null); 
        setSlipPreview(null); 
        setQrImage(null); 
        setRefillAmount(''); 
        loadRefillHistory();
      } else { 
        showNotification(data.message || 'สแกนสลิปไม่ผ่าน', 'error'); 
      }
    } catch (err) { 
      showNotification('เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error'); 
    }
    setLoading(false);
  };

  const confirmPurchase = async () => {
    const product = showBuyModal; 
    setShowBuyModal(null); 
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/orders`, { 
        method: 'POST', 
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        }, 
        body: JSON.stringify({ 
          productId: product._id, 
          productName: product.name, 
          price: product.price 
        }) 
      });
  
      const data = await res.json();
      if (res.ok) { 
        setUser({ ...user, balance: data.newBalance }); 
        showNotification(`✓ ซื้อ ${product.name} สำเร็จ! ไปที่ประวัติเพื่อรับของ`, 'success'); 
        loadOrders(); 
        loadProducts(); // 🔥 เพิ่มบรรทัดนี้เข้าไป 1 บรรทัดครับ! เพื่อสั่งให้รีเฟรชสต็อกหน้าร้าน
      } else { 
        showNotification(data.message, 'error'); 
      }
    } catch (err) {}
  };

const confirmRentalPurchase = async () => {
    if (user.balance < selectedRate.price) {
      showNotification('⚠ ยอดเงินไม่พอ กรุณาเติมเงิน', 'warning');
      return;
    }
    
    const product = showRentalModal; 
    setShowRentalModal(null); 
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/orders`, { 
        method: 'POST', 
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        }, 
        body: JSON.stringify({ 
          productId: product._id, 
          productName: `${product.name} (เช่า ${selectedRate.days} วัน)`, // บอกแอดมินให้รู้ว่าเช่ากี่วัน
          price: selectedRate.price,
          durationDays: selectedRate.days // 🔥 ยิงจำนวนวันไปหลังบ้าน
        }) 
      });
  
      const data = await res.json();
      if (res.ok) { 
        setUser({ ...user, balance: data.newBalance }); 
        showNotification(`✓ เช่า ${product.name} สำเร็จ! ไปที่ประวัติเพื่อรับของ`, 'success'); 
        loadOrders(); 
        loadProducts();
      } else { 
        showNotification(data.message, 'error'); 
      }
    } catch (err) {}
    setLoading(false);
  };

  const handleAuth = async (e) => {
    e.preventDefault(); 
    setLoading(true);
    try {
      const endpoint = authMode === 'signup' ? '/auth/signup' : '/auth/login';
      const res = await fetch(`${API_URL}${endpoint}`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify(formData) 
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('token', data.token); 
        setUser(data.user); 
        setIsGuest(false); 
        setCurrentPage('shop'); 
        showNotification('✓ เข้าสู่ระบบสำเร็จ', 'success'); 
        setFormData({ username: '', email: '', password: '' });
      } else { 
        showNotification(data.message || 'ข้อมูลไม่ถูกต้อง', 'error'); 
      }
    } catch (err) { 
      showNotification('เกิดข้อผิดพลาด', 'error'); 
    }
    setLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passForm.newPassword !== passForm.confirmPassword) return showNotification('⚠ รหัสผ่านใหม่ไม่ตรงกัน!', 'error');
    if (passForm.newPassword.length < 6) return showNotification('⚠ รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร', 'warning');
    showNotification('⏳ ระบบกำลังประมวลผล...', 'info');
    setTimeout(() => { 
      showNotification('✓ เปลี่ยนรหัสผ่านสำเร็จ', 'success'); 
      setPassForm({ oldPassword: '', newPassword: '', confirmPassword: '' }); 
    }, 1000);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsGuest(false);
    setCurrentPage('landing'); 
    showNotification('✓ ออกจากระบบสำเร็จ', 'success');
  };

  const showNotification = (msg, type = 'info') => {
    setNotification({ msg, type }); 
    setTimeout(() => setNotification(null), 3000);
  };

  // 🔥 จัดกลุ่มหมวดหมู่ใหม่ แยก Steam Offline กับ ไอดีเช่า ออกจากกัน
  const processedProducts = products.map(p => {
    let newCategory = p.category;
    
    // ถ้าป้ายกำกับคือ 'ไอดีเช่า' ให้แยกปุ่มเป็นหมวด Steam ไอดีเช่า
    if (p.badge === 'ไอดีเช่า') {
      newCategory = 'Steam ไอดีเช่า';
    } 
    // ถ้าเป็น Steam Game ปกติ ให้เปลี่ยนชื่อปุ่มเป็น Steam Offline
    else if (p.category === 'Steam Game' || p.category === 'STEAM OFFLINE') {
      newCategory = 'Steam Offline';
    }
    
    return { ...p, category: newCategory };
  });

  const filteredProducts = selectedCategory === 'ทั้งหมด' 
    ? processedProducts 
    : processedProducts.filter(p => p.category === selectedCategory);
  
  const categories = ['ทั้งหมด', ...new Set(processedProducts.map(p => p.category))];

  const filteredAdminUsers = adminUsers.filter(u =>
    `${u.username}#${u.tag}`.toLowerCase().includes(userSearch.toLowerCase()) || 
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Kanit:wght@300;400;500;700;900&family=Poppins:wght@400;600;800;900&display=swap');
    body { margin: 0; padding: 0; font-family: 'Kanit', sans-serif; background-color: #07070a; color: #fff; }
    .font-poppins, h1, .eng-num { font-family: 'Poppins', sans-serif; }
    
    .logo-glitch { position: relative; display: inline-block; transition: all 0.3s ease; }
    .logo-glitch:hover { filter: drop-shadow(0 0 15px rgba(234, 179, 8, 0.6)); transform: scale(1.02); }
    .logo-shine { 
      background: linear-gradient(120deg, #fff 30%, #d4af37 50%, #fff 70%); 
      background-size: 200% auto; 
      -webkit-background-clip: text; 
      -webkit-text-fill-color: transparent; 
      animation: shine 3s linear infinite; 
    }
    @keyframes shine { to { background-position: 200% center; } }
    
    .glass-panel { 
      background: rgba(15, 15, 18, 0.6); 
      backdrop-filter: blur(12px); 
      -webkit-backdrop-filter: blur(12px); 
      border: 1px solid rgba(255, 255, 255, 0.03); 
      box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.3); 
    }
    .smooth-hover { transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1); }
    .smooth-hover:hover { 
      transform: translateY(-4px); 
      border-color: rgba(212, 175, 55, 0.4); 
      box-shadow: 0 15px 35px -10px rgba(212, 175, 55, 0.2); 
    }
    .smooth-btn { transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1); }
    .smooth-btn:hover { 
      transform: translateY(-2px); 
      box-shadow: 0 8px 20px -5px rgba(212, 175, 55, 0.3); 
    }
    .smooth-btn:active { transform: scale(0.96); }
    .nav-btn { 
      position: relative; 
      padding: 0.75rem 1.25rem;
      border-radius: 1rem; 
      cursor: pointer; 
      transition: all 0.3s ease; 
      color: #9ca3af; 
      font-weight: 500;
      white-space: nowrap;
      display: inline-flex;
      align-items: center; 
      justify-content: center;
    }
    .nav-btn.active { 
      color: #d4af37; 
      background: rgba(212, 175, 55, 0.1); 
      font-weight: 700; 
      border: 1px solid rgba(212, 175, 55, 0.2); 
    }
    .nav-btn:hover:not(.active) { 
      color: #fff; 
      background: rgba(255, 255, 255, 0.05); 
    }
    .fade-in { animation: fadeIn 0.5s cubic-bezier(0.25, 0.8, 0.25, 1) forwards; }
    @keyframes fadeIn { 
      from { opacity: 0; transform: translateY(10px); } 
      to { opacity: 1; transform: translateY(0); } 
    }
    .key-box { 
      background: repeating-linear-gradient( 45deg, rgba(0,0,0,0.2), rgba(0,0,0,0.2) 10px, rgba(20,20,20,0.2) 10px, rgba(20,20,20,0.2) 20px ); 
    }
  `;

  if (currentPage === 'landing') {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden px-4">
        <style>{globalStyles}</style>
        <ParticlesBackground />
        
        <div className="z-10 max-w-4xl w-full text-center fade-in">
          <div className="inline-flex items-center gap-2 bg-black/50 border border-white/10 px-5 py-2 rounded-full text-sm text-gray-300 mb-8 backdrop-blur-md">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_#22c55e]"></div>
            ระบบพร้อมให้บริการ 24/7
          </div>

          <div className="logo-glitch group flex flex-col items-center justify-center mb-6">
            <h1 className="text-6xl md:text-8xl font-black tracking-wider logo-shine uppercase font-poppins drop-shadow-2xl">
              SOCIETY<span className="text-white">×</span>SHOP
            </h1>
          </div>

          <h2 className="text-3xl md:text-5xl font-black mb-6 text-white drop-shadow-lg leading-tight">
            ครบวงจร <span className="text-yellow-500">ราคาจับต้องได้</span>
          </h2>
          <p className="text-gray-400 text-lg md:text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
            โปรแกรมคุณภาพสำหรับเกม PUBG FIVEM และบริการเกมยอดนิยม<br className="hidden md:block"/>
            ซื้อง่าย ได้ไว ส่งอัตโนมัติผ่านเว็บไซต์ตลอด 24 ชั่วโมง
          </p>

          <div className="flex flex-col md:flex-row justify-center items-center gap-4 mb-20">
            <button 
              onClick={() => { setCurrentPage('auth'); setAuthMode('login'); }} 
              className="smooth-btn bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold px-8 py-4 rounded-xl shadow-[0_0_20px_rgba(212,175,55,0.4)] w-full md:w-auto flex items-center justify-center gap-2 text-lg"
            >
              เริ่มต้นใช้งาน <ArrowRight size={20} />
            </button>
            <button 
              onClick={() => { setIsGuest(true); setCurrentPage('shop'); }} 
              className="smooth-btn bg-white/5 border border-white/10 text-white hover:bg-white/10 font-bold px-8 py-4 rounded-xl w-full md:w-auto flex items-center justify-center gap-2 text-lg backdrop-blur-sm"
            >
              <ShoppingBag size={20} /> เข้าชมร้านค้า (Guest)
            </button>
            <button 
              onClick={() => { setIsGuest(true); setCurrentPage('contact'); }} 
              className="smooth-btn bg-[#5865F2]/20 border border-[#5865F2]/50 text-[#5865F2] hover:bg-[#5865F2] hover:text-white font-bold px-8 py-4 rounded-xl w-full md:w-auto flex items-center justify-center gap-2 transition-colors text-lg"
            >
               <Phone size={20} /> ติดต่อเรา
            </button>
          </div>

          <div className="grid grid-cols-3 gap-4 md:gap-12 border-t border-white/10 pt-10 max-w-2xl mx-auto">
            <div className="text-center">
              <h3 className="text-2xl md:text-4xl font-black text-white eng-num mb-1">600+</h3>
              <p className="text-xs md:text-sm text-gray-500 font-bold">ผู้ใช้งาน</p>
            </div>
            <div className="text-center border-l border-r border-white/5">
              <h3 className="text-2xl md:text-4xl font-black text-yellow-400 eng-num mb-1">150+</h3>
              <p className="text-xs md:text-sm text-gray-500 font-bold">ออเดอร์ที่ส่งมอบ</p>
            </div>
            <div className="text-center">
              <h3 className="text-2xl md:text-4xl font-black text-white eng-num mb-1">24/7</h3>
              <p className="text-xs md:text-sm text-gray-500 font-bold">เปิดให้บริการ</p>
            </div>
          </div>

        </div>
      </div>
    );
  }

  if (currentPage === 'auth' && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
        <style>{globalStyles}</style>
        <ParticlesBackground />

        <div className="glass-panel w-full max-w-md rounded-3xl p-8 fade-in relative z-10 border border-yellow-500/20 shadow-[0_0_50px_rgba(212,175,55,0.05)]">
          <button 
            onClick={() => setCurrentPage('landing')} 
            className="absolute top-6 left-6 text-gray-500 hover:text-white transition-colors"
          >
            ← กลับ
          </button>

          <div className="logo-glitch group flex flex-col items-center justify-center mb-2 mt-6">
            <h1 className="text-4xl font-black tracking-wider logo-shine uppercase font-poppins">
              SOCIETY<span className="text-white">×</span>SHOP
            </h1>
            <div className="flex gap-1 mt-1 opacity-50 group-hover:opacity-100 transition-opacity">
              <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse"></div>
              <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse [animation-delay:0.2s]"></div>
              <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse [animation-delay:0.4s]"></div>
            </div>
          </div>
          <p className="text-gray-400 text-center text-sm mb-8 tracking-widest font-poppins">PREMIUM STORE</p>

          <div className="flex gap-2 mb-6 bg-black/40 p-1 rounded-xl">
            <button 
              onClick={() => setAuthMode('login')} 
              className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all duration-300 ${authMode === 'login' ? 'bg-yellow-500 text-black shadow-md' : 'text-gray-400 hover:text-white'}`}
            >
              เข้าสู่ระบบ
            </button>
            <button 
              onClick={() => setAuthMode('signup')} 
              className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all duration-300 ${authMode === 'signup' ? 'bg-yellow-500 text-black shadow-md' : 'text-gray-400 hover:text-white'}`}
            >
              สมัครสมาชิก
            </button>
          </div>

          {authMode === 'forgot' ? (
            <div className="fade-in space-y-4">
              <p className="text-gray-400 text-sm text-center mb-4">กรอกอีเมลที่ใช้สมัคร เพื่อรับรหัสผ่านชั่วคราว (OTP)</p>
              <input 
                type="email" 
                placeholder="อีเมลของคุณ (Email)" 
                value={forgotEmail} 
                onChange={(e) => setForgotEmail(e.target.value)} 
                className="w-full px-5 py-3.5 bg-black/40 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-yellow-500 transition-all placeholder-gray-500" 
                required
              />
              <button 
                onClick={async () => {
                  if(!forgotEmail) return showNotification('กรุณากรอกอีเมล', 'warning');
                  setLoading(true);
                  try {
                    const res = await fetch(`${API_URL}/auth/forgot-password`, { 
                      method: 'POST', 
                      headers: { 'Content-Type': 'application/json' }, 
                      body: JSON.stringify({ email: forgotEmail }) 
                    });
                    const data = await res.json();
                    if(res.ok) { 
                      showNotification(data.message, 'success'); 
                      setAuthMode('reset'); 
                    } else {
                      showNotification(data.message, 'error');
                    }
                  } catch(e) { 
                    showNotification('เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error'); 
                  }
                  setLoading(false);
                }} 
                disabled={loading} 
                className="smooth-btn w-full mt-2 py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold text-lg rounded-xl shadow-[0_0_15px_rgba(212,175,55,0.3)] disabled:opacity-50"
              >
                {loading ? 'กำลังส่งอีเมล...' : 'ส่งรหัส OTP'}
              </button>
              <button onClick={() => setAuthMode('login')} className="w-full mt-4 text-gray-500 text-sm hover:text-white transition-colors">
                ← กลับไปหน้าเข้าสู่ระบบ
              </button>
            </div>
          ) : authMode === 'reset' ? (
            <div className="fade-in space-y-4">
              <p className="text-gray-400 text-sm text-center mb-4">กรอกรหัส OTP จากอีเมล และตั้งรหัสผ่านใหม่</p>
              <input 
                type="text" 
                placeholder="รหัส OTP 6 หลัก" 
                value={resetData.otp} 
                onChange={(e) => setResetData({...resetData, otp: e.target.value})} 
                className="w-full px-5 py-3.5 bg-black/40 border border-gray-700/50 rounded-xl text-white tracking-widest text-center text-lg focus:outline-none focus:border-yellow-500 transition-all placeholder-gray-500" 
                required 
                maxLength={6}
              />
              <input 
                type="password" 
                placeholder="รหัสผ่านใหม่" 
                value={resetData.newPassword} 
                onChange={(e) => setResetData({...resetData, newPassword: e.target.value})} 
                className="w-full px-5 py-3.5 bg-black/40 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-yellow-500 transition-all placeholder-gray-500" 
                required
              />
              <button 
                onClick={async () => {
                  if(!resetData.otp || !resetData.newPassword) return showNotification('กรอกข้อมูลให้ครบ', 'warning');
                  setLoading(true);
                  try {
                    const res = await fetch(`${API_URL}/auth/reset-password`, { 
                      method: 'POST', 
                      headers: { 'Content-Type': 'application/json' }, 
                      body: JSON.stringify({ email: forgotEmail, otp: resetData.otp, newPassword: resetData.newPassword }) 
                    });
                    const data = await res.json();
                    if(res.ok) { 
                      showNotification(data.message, 'success'); 
                      setAuthMode('login'); 
                      setResetData({otp:'', newPassword:''}); 
                      setForgotEmail(''); 
                    } else {
                      showNotification(data.message, 'error');
                    }
                  } catch(e) { 
                    showNotification('เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error'); 
                  }
                  setLoading(false);
                }} 
                disabled={loading} 
                className="smooth-btn w-full mt-2 py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold text-lg rounded-xl shadow-[0_0_15px_rgba(212,175,55,0.3)] disabled:opacity-50"
              >
                {loading ? 'กำลังเปลี่ยนรหัส...' : 'ยืนยันรหัสผ่านใหม่'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleAuth} className="space-y-4">
              <div>
                <input 
                  type="text" 
                  placeholder="ชื่อผู้ใช้งาน (Username)" 
                  value={formData.username} 
                  onChange={(e) => setFormData({...formData, username: e.target.value})} 
                  className="w-full px-5 py-3.5 bg-black/40 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-yellow-500 transition-all placeholder-gray-500" 
                  required
                />
              </div>
              {authMode === 'signup' && (
                <div className="fade-in">
                  <input 
                    type="email" 
                    placeholder="อีเมล (Email)" 
                    value={formData.email} 
                    onChange={(e) => setFormData({...formData, email: e.target.value})} 
                    className="w-full px-5 py-3.5 bg-black/40 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-yellow-500 transition-all placeholder-gray-500" 
                    required
                  />
                </div>
              )}
              <div>
                <input 
                  type="password" 
                  placeholder="รหัสผ่าน (Password)" 
                  value={formData.password} 
                  onChange={(e) => setFormData({...formData, password: e.target.value})} 
                  className="w-full px-5 py-3.5 bg-black/40 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-yellow-500 transition-all placeholder-gray-500" 
                  required
                />
              </div>

              {authMode === 'login' && (
                <div className="flex justify-end">
                  <button 
                    type="button" 
                    onClick={() => setAuthMode('forgot')} 
                    className="text-gray-400 text-sm hover:text-yellow-500 transition-colors"
                  >
                    ลืมรหัสผ่าน?
                  </button>
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading} 
                className="smooth-btn w-full mt-6 py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold text-lg rounded-xl shadow-[0_0_15px_rgba(212,175,55,0.3)] disabled:opacity-50"
              >
                {loading ? 'กำลังประมวลผล...' : (authMode === 'signup' ? 'ยืนยันสมัครสมาชิก' : 'เข้าสู่ระบบ')}
              </button>
            </form>
          )}

        </div>

        {notification && (
          <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-6 py-4 rounded-xl font-bold glass-panel fade-in">
            {notification.type === 'success' ? <CheckCircle size={20} className="text-green-400" /> : <AlertCircle size={20} className="text-red-400" />}
            <span className={notification.type === 'success' ? 'text-green-400' : 'text-red-400'}>{notification.msg}</span>
          </div>
        )}
      </div>
    );
  }

  if (!user && !isGuest) return null;

  return (
    <div className="min-h-screen relative overflow-x-hidden">
      <style>{globalStyles}</style>
      <ParticlesBackground />

      <header className="glass-panel border-b-0 border-white/5 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="logo-glitch group cursor-pointer" onClick={() => { isGuest ? setCurrentPage('landing') : setCurrentPage('shop') }}>
            <h1 className="text-2xl font-black tracking-wider logo-shine uppercase font-poppins">
              SOCIETY<span className="text-white">×</span>SHOP
            </h1>
            <div className="flex gap-1 mt-[-2px] ml-1 opacity-50 group-hover:opacity-100 transition-opacity">
              <div className="w-1 h-1 bg-yellow-500 rounded-full animate-pulse"></div>
              <div className="w-1 h-1 bg-yellow-500 rounded-full animate-pulse [animation-delay:0.2s]"></div>
              <div className="w-1 h-1 bg-yellow-500 rounded-full animate-pulse [animation-delay:0.4s]"></div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {isGuest ? (
              <button 
                onClick={() => { setAuthMode('login'); setCurrentPage('auth'); }} 
                className="smooth-btn px-6 py-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-xl shadow-md"
              >
                เข้าสู่ระบบ
              </button>
            ) : (
              <>
                <div className="bg-black/40 border border-white/5 rounded-xl px-5 py-2">
                  <span className="text-gray-400 text-xs block">ยอดเงินคงเหลือ</span>
                  <p className="text-yellow-400 font-bold eng-num text-lg leading-tight">฿{user?.balance.toFixed(2)}</p>
                </div>
                <button 
                  onClick={logout} 
                  className="smooth-btn flex items-center gap-2 px-4 py-3 bg-red-500/10 text-red-400 border border-red-500/20 rounded-xl hover:bg-red-500 hover:text-white"
                >
                  <LogOut size={18} />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="border-t border-white/5 bg-black/20">
          <div className="max-w-6xl mx-auto px-6 py-2 flex gap-2 overflow-x-auto">
            {user?.isAdmin && (
              <button onClick={() => { setCurrentPage('admin'); loadAdminData(); }} className={`nav-btn ${currentPage === 'admin' ? 'active' : ''}`}>
                <ShieldCheck size={18} className="inline mr-2 mb-1" /> จัดการหลังบ้าน
              </button>
            )}

            <button onClick={() => setCurrentPage('shop')} className={`nav-btn ${currentPage === 'shop' ? 'active' : ''}`}>
              <ShoppingBag size={18} className="inline mr-2 mb-1" /> ร้านค้า
            </button>
            
            {!isGuest && (
              <>
                <button onClick={() => { setCurrentPage('refill'); loadRefillHistory(); }} className={`nav-btn ${currentPage === 'refill' ? 'active' : ''}`}>
                  <Wallet size={18} className="inline mr-2 mb-1" /> เติมเงิน
                </button>
                <button onClick={() => { setCurrentPage('orders'); loadOrders(); }} className={`nav-btn ${currentPage === 'orders' ? 'active' : ''}`}>
                  <Clock size={18} className="inline mr-2 mb-1" /> ประวัติการซื้อ
                </button>
                <button onClick={() => setCurrentPage('profile')} className={`nav-btn ${currentPage === 'profile' ? 'active' : ''}`}>
                  <User size={18} className="inline mr-2 mb-1" /> ข้อมูลผู้ใช้
                </button>
              </>
            )}
            
            <button onClick={() => setCurrentPage('contact')} className={`nav-btn ${currentPage === 'contact' ? 'active' : ''}`}>
              <Phone size={18} className="inline mr-2 mb-1" /> ติดต่อเรา
            </button>
            <button onClick={() => setCurrentPage('guide')} className={`nav-btn ${currentPage === 'guide' ? 'active' : ''}`}>
              📖 คู่มือการใช้งาน
            </button>
            <button onClick={() => setCurrentPage('reviews')} className={`nav-btn ${currentPage === 'reviews' ? 'active' : ''}`}>
              ⭐ รีวิวจากลูกค้า
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 relative z-10">
        
        {currentPage === 'shop' && (
          <div className="fade-in">
            {announcements.length > 0 && (
              <div className="space-y-3 mb-8">
                {announcements.map((item) => (
                  <div key={item._id} className="glass-panel border border-yellow-500/30 bg-yellow-500/5 px-6 py-4 rounded-2xl flex items-center justify-between fade-in">
                    <div className="flex items-center gap-3">
                      <span className="text-yellow-500 text-xl">📢</span>
                      <p className="text-gray-200 text-sm md:text-base font-medium">{item.message}</p>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap">{new Date(item.createdAt).toLocaleDateString('th-TH')}</span>
                  </div>
                ))}
              </div>
            )}

            {isGuest && (
               <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-500 px-6 py-4 rounded-2xl mb-6 flex items-center justify-between">
                 <span className="font-bold flex items-center gap-2"><User size={20}/> โหมดเยี่ยมชม (Guest)</span>
                 <button onClick={() => setCurrentPage('auth')} className="text-sm underline hover:text-white">
                   เข้าสู่ระบบเพื่อใช้งานเต็มรูปแบบ
                 </button>
               </div>
            )}

            <h2 className="text-3xl font-black mb-6">ร้านค้า (SHOP)</h2>
            
            {/* 🔥 ช่องค้นหาสินค้า */}
            <div className="mb-6 w-full max-w-md">
              <div className="relative">
                <input
                  type="text"
                  placeholder="🔍 ค้นหาชื่อเกม..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-900/80 border border-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-yellow-500 transition-colors"
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="flex gap-3 mb-8 overflow-x-auto py-6 px-2 -my-4 -mx-2 scrollbar-hide">
              {categories.map(cat => (
                <button 
                  key={cat} 
                  onClick={() => setSelectedCategory(cat)} 
                  className={`smooth-btn px-6 py-2.5 rounded-xl whitespace-nowrap transition-all ${
                    selectedCategory === cat 
                      ? 'bg-yellow-500 text-black font-bold shadow-[0_0_20px_rgba(234,179,8,0.5)] scale-105' 
                      : 'glass-panel text-gray-300 hover:text-white border border-white/5 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts
                .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0)) // 🔥 ดันเกมที่ปักหมุดขึ้นมาอยู่อันดับแรกสุดเสมอ
                .map((product, index) => (
                <div key={product._id} className={`smooth-hover glass-panel rounded-2xl p-4 flex flex-col border-white/5 overflow-hidden group relative ${product.isPinned ? 'border-yellow-500/50 shadow-[0_0_20px_rgba(212,175,55,0.15)]' : ''}`} style={{animationDelay: `${index * 0.1}s`}}>
                  
                  {/* 🔥 ถ้าเกมไหนถูกปักหมุด ให้มีป้ายพิเศษโชว์เด่นๆ */}
                  {product.isPinned && (
                    <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-yellow-500 to-amber-600 text-black px-3 py-1 rounded-full text-xs font-black shadow-lg flex items-center gap-1">
                      ⭐ เกมฮิตแนะนำ
                    </div>
                  )}

                  {product.image ? (
                    
                    <div className="w-full h-48 mb-4 rounded-xl overflow-hidden relative">
                      <img 
                      
                        src={product.image} 
                        alt={product.name} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#151518] via-transparent to-transparent"></div>
                      
                      {/* กล่องใส่ป้ายเตือนต่างๆ */}
                      <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                        {product.badge && (
                          <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-black shadow-lg">
                            {product.badge}
                          </div>
                        )}
                        
                        {/* 🔥 ป้ายเตือน Denuvo */}
                        {product.denuvo && (
                          <div className="bg-red-600/90 text-white px-3 py-1 rounded-full text-[10px] font-bold shadow-lg animate-pulse flex items-center gap-1 backdrop-blur-sm border border-red-400/50">
                            <AlertCircle size={12} /> ติด Denuvo
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-end mb-2">
                      {product.badge && <span className="bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 px-3 py-1 rounded-full text-xs font-bold mt-2 mr-2">{product.badge}</span>}
                    </div>
                  )}
                  
                  <div className="flex-1 flex flex-col justify-between px-2">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{product.name}</h3>
                      <p className="text-gray-400 text-sm mb-6 leading-relaxed line-clamp-2">{product.description}</p>
                    </div>
                    
                    <div className="flex justify-between items-end mt-4 pt-4 border-t border-white/10">
                    <div>
                      <span className="text-2xl font-black text-yellow-400 eng-num block">฿{product.price}</span>
                      
                      <div className="mt-1 space-y-0.5">
                        <span className="text-[10px] text-gray-500 eng-num flex items-center gap-1 uppercase tracking-tighter">
                          <ShoppingBag size={10} /> ขายแล้ว {product.soldCount || 0} ชิ้น
                        </span>
                        {/* 🔥 เช็กว่าเป็นไอดีเช่าให้เปลี่ยนข้อความเตือนให้เช็กคิว */}
                        {product.badge === 'ไอดีเช่า' ? (
                          <span className="text-[10px] text-yellow-400 font-bold flex items-center gap-1">
                             🔍 กดปุ่มสั่งซื้อเพื่อเช็กคิวว่าง
                          </span>
                        ) : product.stock === 'unlimited' ? (
                          <span className="text-[10px] text-green-500 font-bold flex items-center gap-1">
                             ✓ สินค้าพร้อมส่ง
                          </span>
                        ) : (
                          <span className={`text-[10px] font-bold flex items-center gap-1 ${product.stock > 0 ? 'text-blue-400' : 'text-red-500'}`}>
                             {product.stock > 0 ? `📦 คงเหลือ ${product.stock} ชิ้น` : '✗ สินค้าหมด'}
                          </span>
                        )}
                      </div>
                    </div>

                    <button 
                      onClick={() => handleBuy(product)} 
                      disabled={product.stock === 0}
                      className={`smooth-btn font-bold px-6 py-2.5 rounded-xl shadow-[0_0_15px_rgba(212,175,55,0.2)] ${
                        product.stock === 0 
                        ? 'bg-gray-700 text-gray-400 cursor-not-allowed shadow-none' 
                        : 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black'
                      }`}
                    >
                      {product.stock === 0 ? 'สินค้าหมด' : 'สั่งซื้อ'}
                    </button>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

{currentPage === 'guide' && (
          <div className="fade-in space-y-8 pb-12">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="text-3xl md:text-4xl font-black mb-3">คู่มือการใช้งาน <span className="text-yellow-500">(USER GUIDE)</span></h2>
              <p className="text-gray-400 text-sm md:text-base">ขั้นตอนการเข้าเล่นเกม Steam Offline และ ไอดีเช่า อ่านทำความเข้าใจก่อนเริ่มใช้งาน</p>
              <div className="w-20 h-1 bg-yellow-500 rounded-full mx-auto mt-4"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* คู่มือ Steam Offline */}
              <div className="glass-panel p-8 rounded-3xl border-white/5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl"></div>
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <ShoppingBag className="text-blue-400"/> แบบซื้อขาด (Steam Offline)
                </h3>
                <ul className="space-y-4 text-gray-300 text-sm">
                  <li className="flex gap-3">
                    <span className="bg-blue-500/20 text-blue-400 w-6 h-6 rounded-full flex items-center justify-center shrink-0 font-bold">1</span>
                    <p>ล็อกอินเข้า Steam ด้วย ไอดี และ รหัสผ่าน ที่ได้รับจากหน้าประวัติการสั่งซื้อ</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-blue-500/20 text-blue-400 w-6 h-6 rounded-full flex items-center justify-center shrink-0 font-bold">2</span>
                    <p>ไปที่หน้าคลังเกม (Library) กดดาวน์โหลดและติดตั้งเกมให้เรียบร้อย</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-blue-500/20 text-blue-400 w-6 h-6 rounded-full flex items-center justify-center shrink-0 font-bold">3</span>
                    <p>เมื่อเข้าเกมได้แล้ว ให้ออกมาที่หน้าต่าง Steam กดที่เมนู Steam (มุมซ้ายบน) เลือก <b>"Go Offline..."</b> (เข้าสู่โหมดออฟไลน์)</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-blue-500/20 text-blue-400 w-6 h-6 rounded-full flex items-center justify-center shrink-0 font-bold">4</span>
                    <p>สามารถเล่นเกมต่อในโหมดออฟไลน์ได้ตลอดชีพ (ห้ามปรับกลับเป็นออนไลน์เด็ดขาด)</p>
                  </li>
                </ul>
              </div>

              {/* คู่มือ ไอดีเช่า & Denuvo */}
              <div className="glass-panel p-8 rounded-3xl border-yellow-500/20 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-500/10 rounded-full blur-2xl"></div>
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Clock className="text-yellow-400"/> แบบไอดีเช่า & แก้ปัญหา Denuvo
                </h3>
                <div className="bg-red-500/10 border border-red-500/20 p-3 rounded-xl mb-4">
                  <p className="text-red-400 text-xs font-bold mb-1">⚠️ ระบบ Denuvo คืออะไร?</p>
                  <p className="text-gray-400 text-[11px] leading-relaxed">เกมที่มี Denuvo จะจำกัดการล็อกอินเครื่องใหม่แค่ 5 เครื่อง/วัน หากคิวเต็มต้องรอ 24 ชม. จึงเป็นเหตุผลว่าทำไมต้องใช้ระบบ "เช่า" เพื่อจองคิวเข้าเกมครั้งแรก</p>
                </div>
                <ul className="space-y-4 text-gray-300 text-sm">
                  <li className="flex gap-3">
                    <span className="bg-yellow-500/20 text-yellow-400 w-6 h-6 rounded-full flex items-center justify-center shrink-0 font-bold">1</span>
                    <p>เลือกจำนวนวันเช่าตามต้องการ และนำไอดีไปล็อกอิน Steam</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-yellow-500/20 text-yellow-400 w-6 h-6 rounded-full flex items-center justify-center shrink-0 font-bold">2</span>
                    <p>หาก Steam ถามหา Guard ให้กดปุ่ม <b>"ขอโค้ด Steam Guard"</b> ในหน้าประวัติการสั่งซื้อ</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-yellow-500/20 text-yellow-400 w-6 h-6 rounded-full flex items-center justify-center shrink-0 font-bold">3</span>
                    <p>โหลดเกมและเข้าเกมครั้งแรกให้สำเร็จ </p>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-yellow-500/20 text-yellow-400 w-6 h-6 rounded-full flex items-center justify-center shrink-0 font-bold">4</span>
                    <p>เข้าเกมได้แล้ว <b>สามารถเล่นแบบออนไลน์ (Online Mode) ได้ตามปกติ</b> ตลอดระยะเวลาที่เช่า</p>
                  </li>
                </ul>
              </div>
            </div>

            <div className="glass-panel p-6 rounded-2xl border-red-500/20 bg-red-500/5 max-w-4xl mx-auto mt-8">
              <h4 className="text-red-400 font-bold mb-2 flex items-center gap-2">
                <AlertCircle size={18} /> กฎเหล็กข้อห้าม (หากฝ่าฝืนตัดสิทธิ์การใช้งานทันที)
              </h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                ❌ ห้ามเปลี่ยน รหัสผ่าน / อีเมล / ชื่อโปรไฟล์ ในบัญชี Steam เด็ดขาด<br/>
                ❌ ห้ามใช้โปรแกรมช่วยเล่น (Hack/Cheat) หรือทำให้ไอดีโดนแบน (แบน VAC)<br/>
                ❌ <b>สำหรับเกม Steam Offline (ซื้อขาด)</b>: ห้ามเล่นโหมดออนไลน์ ให้ปรับเป็นออฟไลน์เสมอ<br/>
                ❌ ห้ามนำบัญชีไปแจกจ่าย หรือจำหน่ายต่อให้ผู้อื่น
              </p>
            </div>
          </div>
        )}

        {currentPage === 'contact' && (
          <div className="fade-in">
            <h2 className="text-3xl font-black mb-6">ติดต่อเรา (CONTACT)</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a href={STORE_LINKS.discord} target="_blank" rel="noreferrer" className="glass-panel rounded-3xl p-8 flex flex-col items-center justify-center text-center smooth-hover border-white/5 hover:border-[#5865F2]/50 group">
                 <div className="w-20 h-20 bg-[#5865F2]/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#5865F2] transition-colors">
                   <MessageSquare size={36} className="text-[#5865F2] group-hover:text-white" />
                 </div>
                 <h3 className="text-2xl font-bold text-white mb-2">Discord</h3>
                 <p className="text-gray-400 text-sm">แจ้งปัญหา เปิดทิคเก็ต พูดคุยกับแอดมิน</p>
              </a>
              <a href={STORE_LINKS.tiktok} target="_blank" rel="noreferrer" className="glass-panel rounded-3xl p-8 flex flex-col items-center justify-center text-center smooth-hover border-white/5 hover:border-pink-500/50 group">
                 <div className="w-20 h-20 bg-pink-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-pink-500 transition-colors">
                   <Smartphone size={36} className="text-pink-500 group-hover:text-white" />
                 </div>
                 <h3 className="text-2xl font-bold text-white mb-2">TikTok</h3>
                 <p className="text-gray-400 text-sm">ติดตามอัปเดตและไฮไลท์ใหม่ๆ</p>
              </a>
              <a href={STORE_LINKS.youtube} target="_blank" rel="noreferrer" className="glass-panel rounded-3xl p-8 flex flex-col items-center justify-center text-center smooth-hover border-white/5 hover:border-red-500/50 group">
                 <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-red-500 transition-colors">
                   <Video size={36} className="text-red-500 group-hover:text-white" />
                 </div>
                 <h3 className="text-2xl font-bold text-white mb-2">YouTube</h3>
                 <p className="text-gray-400 text-sm">รับชมรีวิวและสอนการใช้งานต่างๆ</p>
              </a>
            </div>
          </div>
        )}

        {currentPage === 'refill' && !isGuest && (
          <div className="fade-in">
            <h2 className="text-3xl font-black mb-6">เติมเงิน (REFILL)</h2>
            <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 shadow-2xl mb-12">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <span className="text-yellow-500">⚡</span> เติมเงินอัตโนมัติ (Dynamic QR)
              </h2>
              {/* 🔥 กล่องเตือนก่อนเติมเงิน */}
              <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl mb-6 fade-in">
                <p className="text-red-400 font-bold text-sm flex items-center gap-2 mb-1">
                  <AlertCircle size={16}/> ประกาศสำคัญก่อนเติมเงิน
                </p>
                <p className="text-gray-400 text-[11px] md:text-xs">
                  สำหรับลูกค้าที่ต้องการซื้อ <b className="text-yellow-400">"ไอดีเช่า"</b> กรุณากลับไปหน้าร้านและ <b>"กดปุ่มสั่งซื้อเพื่อเช็กคิวว่าง"</b> ก่อนทำการเติมเงินทุกครั้ง (สามารถกดเช็กคิวได้แม้ยอดเงินเป็น 0)<br/>ทางร้านขอสงวนสิทธิ์ <u>ไม่รับโอนเงินคืนทุกกรณี</u> หากลูกค้าเติมเงินเข้ามาแล้วคิวเช่าเต็ม
                </p>
              </div>
              {!qrImage ? (
                <div className="space-y-4 fade-in">
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">ระบุจำนวนเงินที่ต้องการเติม (บาท)</label>
                    <input 
                      type="number" 
                      value={refillAmount} 
                      onChange={(e) => setRefillAmount(e.target.value)} 
                      placeholder="เช่น 10, 35, 100 ไม่จำกัดยอดเติมขั้นต่ำ" 
                      className="w-full bg-black/50 border border-white/10 rounded-xl p-4 text-white text-xl font-bold focus:outline-none focus:border-yellow-500 transition-colors"
                    />
                  </div>
                  <button 
                    onClick={handleGenerateQR} 
                    disabled={loading || !refillAmount} 
                    className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-4 rounded-xl transition-all disabled:opacity-50"
                  >
                    {loading ? 'กำลังสร้าง QR Code...' : 'สร้าง QR Code เพื่อจ่ายเงิน'}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 fade-in">
                  <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center text-black">
                    <img src={qrImage} alt="PromptPay QR" className="w-48 h-48 mb-4 border-4 border-gray-100 rounded-xl" />
                    <p className="font-bold text-lg text-center">สแกนจ่ายผ่านแอปธนาคาร</p>
                    <p className="text-2xl font-black text-blue-600 mt-2">฿{expectedAmount}.00</p>
                    <p className="text-sm text-gray-500 mt-2">ชื่อบัญชี: นาย อภิวรรธน์ ภู่ถาวร</p>
                    <button 
                      onClick={() => { setQrImage(null); setRefillAmount(''); setSlipPreview(null); setSlipImage(null); }} 
                      className="mt-4 text-xs text-red-500 underline"
                    >
                      ยกเลิก / เปลี่ยนยอดเงิน
                    </button>
                  </div>
                  <div className="flex flex-col justify-between">
                    <div className="bg-black/30 border border-dashed border-white/20 rounded-2xl p-6 text-center hover:border-yellow-500 transition-colors cursor-pointer relative h-full flex flex-col justify-center min-h-[250px]">
                      <input type="file" accept="image/*" onChange={handleSlipUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                      {slipPreview ? ( 
                        <img src={slipPreview} className="mx-auto max-h-48 object-contain rounded-xl" alt="Slip Preview" /> 
                      ) : (
                        <div className="py-8">
                          <div className="text-yellow-500 text-4xl mb-2">📸</div>
                          <p className="text-gray-300 font-bold">แตะเพื่อเลือกรูปสลิป</p>
                          <p className="text-gray-500 text-sm mt-1">ต้องเป็นสลิปยอด {expectedAmount} บาทเท่านั้น แนะนำไฟล์ JPG หรือ PNG</p>
                        </div>
                      )}
                    </div>
                    <button 
                      onClick={submitSlip} 
                      disabled={!slipImage || loading} 
                      className="w-full mt-4 bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-4 rounded-xl transition-all shadow-[0_0_15px_rgba(234,179,8,0.3)] disabled:opacity-50 disabled:shadow-none"
                    >
                      {loading ? 'กำลังตรวจสอบสลิปด้วย AI...' : 'ยืนยันการเติมเงิน'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="glass-panel border border-yellow-500/20 rounded-2xl p-6 mt-8">
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <span className="text-yellow-500">🎁</span> แลกรับโค้ดส่วนลด / โบนัสเติมเงิน
              </h3>
              <p className="text-gray-400 text-sm mb-4">มีโค้ดโปรโมชันจากทางร้าน สามารถกรอกเพื่อรับเครดิตเงินเพิ่มเข้าบัญชีได้ทันที</p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <input 
                  type="text" 
                  value={promoCodeInput}
                  onChange={(e) => setPromoCodeInput(e.target.value)}
                  placeholder="กรอกโค้ดส่วนลดที่นี่ (เช่น SOCIETY50)" 
                  className="flex-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-white uppercase font-bold tracking-wider outline-none focus:border-yellow-500 transition-all placeholder-gray-500"
                />
                <button 
                  onClick={handleRedeemPromoCode}
                  disabled={loading || !promoCodeInput}
                  className="smooth-btn bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold px-8 py-3.5 rounded-xl shadow-[0_0_15px_rgba(212,175,55,0.2)] disabled:opacity-50 whitespace-nowrap"
                >
                  {loading ? 'กำลังตรวจสอบ...' : 'use code'}
                </button>
              </div>
            </div>

            <div className="fade-in">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2"><Clock className="text-gray-400"/> ประวัติการทำรายการ</h3>
              <div className="space-y-3">
                {refillHistory.length === 0 ? ( 
                  <div className="glass-panel rounded-2xl p-8 text-center text-gray-500 border-white/5">ยังไม่มีข้อมูลการเติมเงิน</div> 
                ) : (
                  refillHistory.map((item, idx) => (
                    <div key={idx} className="smooth-hover glass-panel rounded-2xl p-5 flex justify-between items-center border-white/5">
                      <div className="flex items-center gap-4">
                        <div className="bg-black/40 p-3 rounded-xl border border-white/5">
                          <Wallet className="text-yellow-400" size={20}/>
                        </div>
                        <div>
                          <p className="font-bold text-lg text-white eng-num">฿{item.amount}</p>
                          <p className="text-gray-500 text-sm">{new Date(item.date).toLocaleString('th-TH')}</p>
                        </div>
                      </div>
                      <span className={`px-4 py-1.5 rounded-full text-sm font-bold border ${item.status === 'verified' ? 'bg-green-500/10 text-green-400 border-green-500/20' : item.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                        {item.status === 'verified' ? '✓ สำเร็จ' : item.status === 'pending' ? '⏳ รอดำเนินการ' : '✗ ไม่สำเร็จ'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {currentPage === 'orders' && !isGuest && (
          <div className="fade-in">
            <h2 className="text-3xl font-black mb-6">ประวัติการสั่งซื้อ (ORDERS)</h2>
            <div className="space-y-6">
              {orders.length === 0 ? (
                <div className="glass-panel rounded-3xl p-16 text-center text-gray-500 border-white/5">
                  <ShoppingBag size={56} className="mx-auto mb-4 opacity-20 text-white" />
                  <p className="text-lg">คุณยังไม่เคยสั่งซื้อสินค้า</p>
                </div>
              ) : orders.map((order, idx) => {
                const pName = order.productName.toUpperCase(); 
                let productType = 'admin_install'; 
                
                // เช็กเพิ่ม: ถ้าข้อมูลที่ได้มาขึ้นต้นด้วย ID: แสดงว่าเป็นสินค้า 499K (บัญชีเกม)
                if (order.licenseKey && order.licenseKey.startsWith('ID:')) {
                  productType = 'account_info';
                }
                else if (pName.includes('CMD')) { 
                  productType = 'key_and_download'; 
                } else if (pName.includes('EXTERNAL') || pName.includes('RESHADE') || pName.includes('SYSTEM TUNING') || order.downloadUrl) { 
                  productType = 'download_only'; 
                }
                
                let dLink = order.downloadUrl || "#"; 
                if (dLink === "#") {
                  if (pName.includes('EXTERNAL')) dLink = STORE_LINKS.external; 
                  else if (pName.includes('RESHADE')) dLink = STORE_LINKS.reshade; 
                  else if (pName.includes('CMD')) dLink = STORE_LINKS.cmd;
                  else if (pName.includes('SYSTEM TUNING')) dLink = STORE_LINKS.systempf;
                }
                
                return (
                  <div key={idx} className="glass-panel rounded-2xl p-6 flex flex-col gap-5 border-white/5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <p className="font-bold text-xl text-white mb-1">{order.productName}</p>
                        <p className="text-gray-500 text-sm eng-num">รหัสคำสั่งซื้อ: #{order._id}</p>
                      </div>
                      <div className="sm:text-right flex sm:flex-col items-center sm:items-end justify-between">
                        <p className="text-yellow-400 font-black text-2xl eng-num">฿{order.price}</p>
                        <span className="mt-2 px-3 py-1 rounded-full text-xs font-bold border bg-green-500/10 text-green-400 border-green-500/20">
                          ✓ สำเร็จ (รับสินค้าแล้ว)
                        </span>
                      </div>
                    </div>

                    <div className="key-box rounded-xl p-5 border border-yellow-500/20 fade-in shadow-inner">
                      <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                        <Key size={16} className="text-yellow-400"/> สถานะการรับสินค้า
                      </h4>
                      
                      {productType === 'admin_install' && (
                        <div className="bg-black/60 p-6 rounded-xl border border-white/5 text-center flex flex-col items-center justify-center">
                          <AlertCircle size={36} className="text-yellow-400 mb-3" />
                          <p className="text-white font-bold text-lg mb-1">สินค้านี้ต้องให้ Admin ติดตั้งให้</p>
                          <p className="text-gray-400 text-sm mb-5">กรุณาเข้าร่วม Discord เปิดทิคเก็ต และส่งรหัสคำสั่งซื้อนี้ให้ Admin</p>
                          <a href={STORE_LINKS.discord} target="_blank" rel="noreferrer" className="smooth-btn bg-[#5865F2] hover:bg-[#4752C4] text-white px-8 py-3 rounded-xl font-bold transition-all shadow-lg flex items-center gap-2 w-fit mx-auto">
                            💬 ติดต่อ Admin ใน Discord
                          </a>
                        </div>
                      )}
                      
                     {productType === 'account_info' && (
                        <div className="bg-black/60 p-6 rounded-xl border border-blue-500/30 flex flex-col justify-center relative overflow-hidden">
                          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl"></div>
                          <span className="text-gray-400 text-sm mb-2 font-bold flex items-center gap-2">
                            🎮 บัญชี Steam ของคุณ (ไอดี | รหัสผ่าน)
                          </span>
                          <code className="text-blue-400 font-bold eng-num text-lg md:text-xl select-all bg-blue-500/10 border border-blue-500/20 px-5 py-3 rounded-lg inline-block w-fit shadow-inner mb-4">
                            {order.licenseKey}
                          </code>
                          
                          {/* ส่วนของ Steam Guard */}
                          <div className="bg-gray-900/80 border border-white/5 p-4 rounded-xl mt-2 relative z-10">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                              <div>
                                <span className="text-white font-bold text-sm block mb-1 flex items-center gap-2">
                                  <ShieldCheck size={16} className="text-green-400"/> Steam Guard Code
                                </span>
                                <span className="text-gray-500 text-xs">หากระบบถามหาโค้ด กดปุ่มเพื่อรับโค้ดเข้าใช้งาน</span>
                              </div>
                              
                              {steamCodes[order._id] ? (
  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
    <div className="bg-green-500/10 border border-green-500/20 px-6 py-2 rounded-lg text-center animate-pulse">
      <span className="text-xs text-green-500 block mb-1">โค้ดของคุณคือ</span>
      <span className="text-2xl font-black text-green-400 tracking-widest eng-num">{steamCodes[order._id]}</span>
    </div>

    {/* ปุ่มขอโค้ด พร้อมตัวนับเวลา (ย้ายมาอยู่มุมขวาในกรอบเดียวกัน) */}
    <button 
      onClick={() => handleGetSteamGuard(order._id)}
      disabled={guardCooldowns[order._id] > 0 || fetchingCode[order._id]}
      className={`smooth-btn px-4 py-2.5 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${
        guardCooldowns[order._id] > 0
          ? 'bg-gray-600/50 text-gray-400 cursor-not-allowed shadow-none'
          : 'bg-green-500 hover:bg-green-400 text-black shadow-[0_0_10px_rgba(34,197,94,0.3)] disabled:opacity-50'
      }`}
    >
      {fetchingCode[order._id] ? (
        'กำลังดึงโค้ด...'
      ) : guardCooldowns[order._id] > 0 ? (
        `รอ ${guardCooldowns[order._id]} วิ`
      ) : (
        'ขอโค้ด Steam Guard'
      )}
    </button>
  </div>
) : (
  <button 
    onClick={() => handleGetSteamGuard(order._id)}
    disabled={guardCooldowns[order._id] > 0 || fetchingCode[order._id]}
    className={`smooth-btn px-4 py-2.5 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${
      guardCooldowns[order._id] > 0
        ? 'bg-gray-600/50 text-gray-400 cursor-not-allowed shadow-none'
        : 'bg-green-500 hover:bg-green-400 text-black shadow-[0_0_10px_rgba(34,197,94,0.3)] disabled:opacity-50'
    }`}
  >
    {fetchingCode[order._id] ? (
      'กำลังดึงโค้ด...'
    ) : guardCooldowns[order._id] > 0 ? (
      `รอ ${guardCooldowns[order._id]} วิ`
    ) : (
      'ขอโค้ด Steam Guard'
    )}
  </button>
)}
                            </div>
                          </div>
                        
                          {/* 🔥 แยกคำเตือนระหว่าง "ไอดีเช่า" กับ "ซื้อขาด" (ย้ายมาไว้ข้างในนี้) */}
                          {order.expiresAt || order.productName.includes('เช่า') ? (
                            <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 rounded-xl mt-4">
                              <p className="text-yellow-400 text-xs font-bold mb-1 flex items-center gap-1"><AlertCircle size={14}/> กฎสำหรับไอดีเช่า (เล่นออนไลน์ได้)</p>
                              <p className="text-gray-400 text-[11px] leading-relaxed">
                                ✓ สามารถเข้าเล่นโหมดออนไลน์ (Online Mode) ร่วมกับผู้อื่นได้ตามปกติ<br/>
                                ❌ ห้ามเปลี่ยน รหัสผ่าน / อีเมล / ชื่อโปรไฟล์ เด็ดขาด<br/>
                                ⚠️ กรุณา <b>ออกจากระบบ (Log out) ก่อนเวลาหมด 5 นาที</b> เพื่อป้องกันปัญหาไอดีค้าง
                              </p>
                            </div>
                          ) : (
                            <div className="bg-red-500/10 border border-red-500/20 p-3 rounded-xl mt-4">
                              <p className="text-red-400 text-xs font-bold mb-1 flex items-center gap-1"><AlertCircle size={14}/> กฎสำหรับเกมซื้อขาด (Steam Offline)</p>
                              <p className="text-gray-400 text-[11px] leading-relaxed">
                                ❌ <b>ห้ามเล่นโหมดออนไลน์เด็ดขาด</b> ให้ปรับ Steam เป็น "Go Offline" ทุกครั้งก่อนเข้าเล่น<br/>
                                ⚠️ กรณีลง Windows ใหม่ กรุณาแจ้งแอดมินและออกจากระบบก่อน มิฉะนั้นจะต้องสั่งซื้อใหม่
                              </p>
                            </div>
                          )}
                          
                          {/* 🔥 เอาหลอดเวลามาวางไว้ตรงนี้เลยครับ! */}
                          <RentalTimer startAt={order.startAt} expiresAt={order.expiresAt} />

                        </div>
                      )}
                      
                      {/* ของเดิมที่อยู่ข้างล่าง ปล่อยไว้เหมือนเดิมครับ */}
                      {productType === 'download_only' && (
                        <div className="bg-black/60 p-6 rounded-xl border border-white/5 flex flex-col sm:flex-row items-center justify-between gap-5">
                          <div>
                            <p className="text-white font-bold text-lg mb-1">ไฟล์ของคุณพร้อมแล้ว 🎉</p>
                            <p className="text-gray-400 text-sm">ดาวน์โหลด แตกไฟล์ และสามารถเปิดใช้งานได้ทันที</p>
                          </div>
                          <a href={dLink} target="_blank" rel="noreferrer" className="smooth-btn flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-3.5 rounded-xl font-bold transition-all whitespace-nowrap shadow-[0_0_15px_rgba(37,99,235,0.3)]">
                            <Download size={20}/> ดาวน์โหลดไฟล์
                          </a>
                        </div>
                      )}
                      
                      {productType === 'key_and_download' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-black/60 p-5 rounded-xl border border-white/5 flex flex-col justify-center relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-500/10 rounded-full blur-xl"></div>
                            <span className="text-gray-400 text-xs mb-2">License Key ของคุณ (คลิกเพื่อคลุมดำ)</span>
                            <code className="text-yellow-400 font-bold eng-num text-xl select-all bg-yellow-500/10 border border-yellow-500/20 px-4 py-2 rounded-lg inline-block w-fit">
                              {order.licenseKey || 'ติดต่อแอดมินเพื่อรับสินค้า'}
                            </code>
                          </div>
                          <div className="flex flex-col gap-3">
                            <a href={dLink} target="_blank" rel="noreferrer" className="smooth-btn flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white p-3.5 rounded-xl font-bold transition-all shadow-lg">
                              <Download size={18}/> โหลดไฟล์โปรแกรม
                            </a>
                            <div className="bg-gray-900/80 border border-white/5 p-3 rounded-xl text-xs text-gray-400 flex items-center justify-between px-4">
                              <span>พบปัญหาการใช้งาน?</span>
                              <a href={STORE_LINKS.discord} target="_blank" rel="noreferrer" className="text-[#5865F2] hover:text-white font-bold transition-all flex items-center gap-1">
                                แจ้ง Admin
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {currentPage === 'profile' && !isGuest && (
          <div className="fade-in">
            <h2 className="text-3xl font-black mb-6">ข้อมูลผู้ใช้งาน (PROFILE)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="glass-panel rounded-3xl p-8 border-white/5">
                <div className="flex items-center gap-5 mb-8 pb-6 border-b border-white/5">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center text-black font-black text-2xl shadow-lg shadow-yellow-500/20">
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">
                      {user.username}
                      {user.tag && <span className="text-gray-500 text-lg ml-1">#{user.tag}</span>}
                    </h3>
                    <span className="bg-white/10 text-gray-300 text-xs px-3 py-1 rounded-full border border-white/10">สมาชิกระบบ</span>
                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <p className="text-gray-500 text-sm mb-2">อีเมลติดต่อ (Email)</p>
                    <p className="font-bold text-white bg-black/40 p-3.5 rounded-xl border border-white/5 eng-num">{user.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-2">ยอดเงินคงเหลือ</p>
                    <p className="text-yellow-400 text-3xl font-black eng-num">฿{user.balance.toFixed(2)}</p>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="glass-panel rounded-3xl p-8 mb-6 border-white/5">
                  <h3 className="text-xl font-bold mb-5 flex items-center gap-2">
                    <CheckCircle className="text-green-400"/> สถิติการใช้งาน
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-black/40 border border-white/5 p-5 rounded-2xl text-center smooth-hover">
                      <p className="text-gray-500 text-sm mb-2">ซื้อไปแล้ว</p>
                      <p className="font-black text-3xl text-white eng-num">{orders.length} <span className="text-lg text-gray-400 font-normal">ชิ้น</span></p>
                    </div>
                    <div className="bg-black/40 border border-white/5 p-5 rounded-2xl text-center smooth-hover">
                      <p className="text-gray-500 text-sm mb-2">เป็นสมาชิกเมื่อ</p>
                      <p className="font-bold text-base text-white mt-3 eng-num">{new Date(user.createdAt).toLocaleDateString('th-TH')}</p>
                    </div>
                  </div>
                </div>
                
                <div className="glass-panel rounded-3xl p-8 border-white/5">
                  <h3 className="text-xl font-bold mb-5 flex items-center gap-2">
                    <Key className="text-yellow-500"/> เปลี่ยนรหัสผ่าน
                  </h3>
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <input 
                      type="password" 
                      placeholder="รหัสผ่านเดิม" 
                      value={passForm.oldPassword} 
                      onChange={e=>setPassForm({...passForm, oldPassword: e.target.value})} 
                      className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white focus:border-yellow-500 transition-all outline-none" 
                      required
                    />
                    <input 
                      type="password" 
                      placeholder="รหัสผ่านใหม่" 
                      value={passForm.newPassword} 
                      onChange={e=>setPassForm({...passForm, newPassword: e.target.value})} 
                      className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white focus:border-yellow-500 transition-all outline-none" 
                      required
                    />
                    <input 
                      type="password" 
                      placeholder="ยืนยันรหัสผ่านใหม่" 
                      value={passForm.confirmPassword} 
                      onChange={e=>setPassForm({...passForm, confirmPassword: e.target.value})} 
                      className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white focus:border-yellow-500 transition-all outline-none" 
                      required
                    />
                    <button type="submit" className="smooth-btn w-full py-3.5 bg-white/10 hover:bg-yellow-500 hover:text-black text-white font-bold rounded-xl transition-all border border-white/10 mt-2">
                      อัปเดตรหัสผ่าน
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentPage === 'reviews' && (
          <div className="fade-in space-y-8 pb-12">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="text-3xl md:text-4xl font-black mb-3">ความประทับใจจาก <span className="text-yellow-500">ลูกค้าจริง</span></h2>
              <p className="text-gray-400 text-sm md:text-base">เสียงยืนยันความนิ่ง ความคุ้มค่า และบริการหลังการขายจากผู้ใช้งานจริงใน Discord</p>
              <div className="w-20 h-1 bg-yellow-500 rounded-full mx-auto mt-4"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="glass-panel p-6 rounded-3xl border-white/5 flex flex-col justify-between smooth-hover">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-bold text-white">Panama2</span>
                    <span className="text-yellow-400 text-xs bg-yellow-500/10 px-2.5 py-1 rounded-full border border-yellow-500/20">⭐⭐⭐⭐⭐</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-6">"เจ้านี้ดีสุด มาโครนิ่งมาก ดีกว่าอีกเจ้าที่ V5 อะ"</p>
                </div>
                <div className="border-t border-white/5 pt-3 text-[11px] text-gray-500 flex justify-between">
                  <span>รีวิวจาก Discord</span>
                  <span>11/8/2569</span>
                </div>
              </div>

              <div className="glass-panel p-6 rounded-3xl border-white/5 flex flex-col justify-between smooth-hover">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-bold text-white">Itameow [RLG]</span>
                    <span className="text-yellow-400 text-xs bg-yellow-500/10 px-2.5 py-1 rounded-full border border-yellow-500/20">⭐⭐⭐⭐⭐</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-6">"นึ่งจัดอย่างกะโปร5555"</p>
                </div>
                <div className="border-t border-white/5 pt-3 text-[11px] text-gray-500 flex justify-between">
                  <span>รีวิวจาก Discord</span>
                  <span>9/9/2569</span>
                </div>
              </div>

              <div className="glass-panel p-6 rounded-3xl border-white/5 flex flex-col justify-between smooth-hover">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-bold text-white">รักในดิส คิดได้ไง.</span>
                    <span className="text-yellow-400 text-xs bg-yellow-500/10 px-2.5 py-1 rounded-full border border-yellow-500/20">⭐⭐⭐⭐⭐</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-6">"ใช้ง่าย คลิปสอนครบ มาโครนิ่งกริ๊บบ"</p>
                </div>
                <div className="border-t border-white/5 pt-3 text-[11px] text-gray-500 flex justify-between">
                  <span>รีวิวจาก Discord</span>
                  <span>10/8/2569</span>
                </div>
              </div>

              <div className="glass-panel p-6 rounded-3xl border-white/5 flex flex-col justify-between smooth-hover">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-bold text-white">Thug</span>
                    <span className="text-yellow-400 text-xs bg-yellow-500/10 px-2.5 py-1 rounded-full border border-yellow-500/20">⭐⭐⭐⭐⭐</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-6">"ซื้อตัว200มาคุ้มมากครับ"</p>
                </div>
                <div className="border-t border-white/5 pt-3 text-[11px] text-gray-500 flex justify-between">
                  <span>รีวิวจาก Discord</span>
                  <span>30/3/2569</span>
                </div>
              </div>

              <div className="glass-panel p-6 rounded-3xl border-white/5 flex flex-col justify-between smooth-hover">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-bold text-white">YIMWAN</span>
                    <span className="text-yellow-400 text-xs bg-yellow-500/10 px-2.5 py-1 rounded-full border border-yellow-500/20">⭐⭐⭐⭐⭐</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-6">"ครับแอดใจดีคุยง่ายและของดีมากครับ"</p>
                </div>
                <div className="border-t border-white/5 pt-3 text-[11px] text-gray-500 flex justify-between">
                  <span>รีวิวจาก Discord</span>
                  <span>29/5/2569</span>
                </div>
              </div>

              <div className="glass-panel p-6 rounded-3xl border-white/5 flex flex-col justify-between smooth-hover">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-bold text-white">Gino Matilda</span>
                    <span className="text-yellow-400 text-xs bg-yellow-500/10 px-2.5 py-1 rounded-full border border-yellow-500/20">⭐⭐⭐⭐⭐</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-6">"คุมเม้าส์นิ่งมาก บริการดีมากๆครับ"</p>
                </div>
                <div className="border-t border-white/5 pt-3 text-[11px] text-gray-500 flex justify-between">
                  <span>รีวิวจาก Discord</span>
                  <span>28/8/2569</span>
                </div>
              </div>
            </div>

            <div className="glass-panel p-8 rounded-3xl border-yellow-500/20 text-center max-w-xl mx-auto mt-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/5 rounded-full blur-2xl"></div>
              <h3 className="text-xl font-bold text-white mb-2">อยากดูรีวิวเพิ่มเติมหรือพูดคุยกับเพื่อนๆ?</h3>
              <p className="text-gray-400 text-sm mb-6">สามารถเข้าไปดูฟีดแบ็กสดๆ และพูดคุยในคอมมูนิตี้ของเราได้ที่ Discord เลยครับ</p>
              <a 
                href={STORE_LINKS.discord} 
                target="_blank" 
                rel="noreferrer" 
                className="smooth-btn inline-flex items-center gap-2 bg-[#5865F2] hover:bg-[#4752C4] text-white px-8 py-3.5 rounded-xl font-bold transition-all shadow-lg"
              >
                💬 เข้าร่วม Discord ของเรา
              </a>
            </div>
          </div>
        )}

        {currentPage === 'admin' && user?.isAdmin && (
          <div className="fade-in space-y-8 pb-10">
            <h2 className="text-3xl font-black mb-6 flex items-center gap-2">👑 ระบบแอดมิน</h2>

            {/* 🔥 ปุ่มดูดของจาก 499K */}
            <button 
              onClick={async () => {
                setLoading(true);
                try {
                  const res = await fetch(`${API_URL}/admin/sync-499k`, { 
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } 
                  });
                  const data = await res.json();
                  if(res.ok) { 
                    showNotification(`✓ ${data.message}`, "success"); 
                    loadProducts(); // รีเฟรชหน้าร้านทันที
                  } else {
                    showNotification(`❌ ${data.message}`, "error"); 
                  }
                } catch (err) {
                  showNotification("❌ เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ", "error");
                }
                setLoading(false);
              }} 
              disabled={loading}
              className="bg-purple-600 text-white px-5 py-3 rounded-xl mb-4 font-bold hover:bg-purple-500 transition-all flex items-center gap-2 shadow-lg shadow-purple-500/20 disabled:opacity-50"
            >
              <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
              {loading ? 'กำลังซิงค์ข้อมูล...' : 'ซิงค์สินค้าจากโกดัง 499K เข้าร้าน'}
            </button>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="glass-panel p-6 rounded-2xl border-white/5">
                <p className="text-gray-400 text-sm">ยอดขายรวม</p>
                <p className="text-2xl font-black text-yellow-500 eng-num">฿{adminStats?.totalSales || 0}</p>
              </div>
              
              <div className="glass-panel p-6 rounded-2xl border-white/5">
                <p className="text-gray-400 text-sm">สมาชิกทั้งหมด</p>
                <p className="text-2xl font-black text-white eng-num">{adminStats?.usersCount || 0} คน</p>
              </div>
            </div>

            <div className="glass-panel p-8 rounded-3xl border-white/5 mt-8">
              <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-6">
                <h3 className="text-xl font-bold flex items-center gap-2"><User size={20}/> จัดการผู้ใช้งาน & เติมเงิน</h3>
                
                <div className="relative">
                  <input 
                    type="text" 
                    placeholder="ค้นหาชื่อ หรือ Tag..." 
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-black/50 border border-white/10 rounded-xl text-white outline-none focus:border-yellow-500 w-full md:w-64"
                  />
                  <Search size={18} className="absolute left-3 top-2.5 text-gray-500" />
                </div>
              </div>

              <div className="glass-panel p-8 rounded-3xl border-white/5 mt-8 mb-8">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">🎁 สร้างโค้ดเติมเงิน (Promo Code)</h3>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const code = e.target.promoCode.value;
                  const bonus = e.target.bonusAmount.value;
                  const uses = e.target.maxUses.value;
                  const expiresAt = e.target.expiresAt.value;

                  try {
                    const res = await fetch(`${API_URL}/admin/promo-codes`, {
                      method: 'POST',
                      headers: { 
                        'Content-Type': 'application/json', 
                        'Authorization': `Bearer ${localStorage.getItem('token')}` 
                      },
                      body: JSON.stringify({ code, bonusAmount: bonus, maxUses: uses, expiresAt })
                    });
                    const data = await res.json();
                    if (res.ok) {
                      showNotification(`✓ ${data.message}`, 'success');
                      e.target.reset();
                    } else {
                      showNotification(data.message || 'สร้างโค้ดไม่สำเร็จ', 'error');
                    }
                  } catch (err) {
                    showNotification('เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error');
                  }
                }} className="space-y-4">
                  <input 
                    type="text" 
                    name="promoCode" 
                    placeholder="ชื่อโค้ด (เช่น FREE50)" 
                    className="w-full bg-black/50 border border-white/10 p-3.5 rounded-xl text-white uppercase outline-none focus:border-yellow-500" 
                    required 
                  />
                  <input 
                    type="number" 
                    name="bonusAmount" 
                    placeholder="จำนวนเงินโบนัสที่จะได้รับ (บาท)" 
                    className="w-full bg-black/50 border border-white/10 p-3.5 rounded-xl text-white outline-none focus:border-yellow-500" 
                    required 
                  />
                  <input 
                    type="number" 
                    name="maxUses" 
                    placeholder="จำนวนครั้งที่ใช้ได้ (เช่น 50 ครั้ง)" 
                    className="w-full bg-black/50 border border-white/10 p-3.5 rounded-xl text-white outline-none focus:border-yellow-500" 
                    defaultValue="100" 
                    required 
                  />
                  <div className="space-y-2">
                    <label className="text-xs text-gray-400">วันและเวลาหมดอายุ (ไม่ใส่ = ไม่มีวันหมดอายุ)</label>
                    <input 
                      type="datetime-local" 
                      name="expiresAt" 
                      className="w-full bg-black/50 border border-white/10 p-3.5 rounded-xl text-white outline-none focus:border-yellow-500" 
                    />
                  </div>
                  <button type="submit" className="w-full py-4 bg-yellow-500 text-black font-black rounded-xl hover:bg-yellow-400 transition-all shadow-lg">
                    ยืนยันการสร้างโค้ด
                  </button>
                </form>
              </div>

              <div className="glass-panel p-8 rounded-3xl border-white/5 mb-8">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">📢 จัดการประกาศหน้าเว็บ (Announcements)</h3>
                
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const message = e.target.announceMessage.value;
                  try {
                    const res = await fetch(`${API_URL}/admin/announcements`, {
                      method: 'POST',
                      headers: { 
                        'Content-Type': 'application/json', 
                        'Authorization': `Bearer ${localStorage.getItem('token')}` 
                      },
                      body: JSON.stringify({ message })
                    });
                    const data = await res.json();
                    if (res.ok) {
                      showNotification(`✓ ${data.message}`, 'success');
                      e.target.reset();
                      loadAnnouncements();
                    } else {
                      showNotification(data.message || 'สร้างประกาศไม่สำเร็จ', 'error');
                    }
                  } catch (err) {
                    showNotification('เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error');
                  }
                }} className="space-y-4 mb-6">
                  <textarea 
                    name="announceMessage" 
                    placeholder="ข้อความประกาศ เช่น แจกโค้ดส่วนลดฟรี 50 บาท รีบใช้ก่อนหมดเขต!" 
                    className="w-full h-24 bg-black/50 border border-white/10 p-4 rounded-xl text-white outline-none focus:border-yellow-500"
                    required
                  ></textarea>
                  <button type="submit" className="w-full py-4 bg-yellow-500 text-black font-black rounded-xl hover:bg-yellow-400 transition-all shadow-lg">
                    โพสต์ประกาศหน้าเว็บ
                  </button>
                </form>

                <div className="space-y-3">
                  {announcements.map((item) => (
                    <div key={item._id} className="bg-black/40 border border-white/5 p-4 rounded-2xl flex items-center justify-between gap-4">
                      <div>
                        <p className="text-white font-medium text-sm">{item.message}</p>
                        <span className="text-xs text-gray-500">{new Date(item.createdAt).toLocaleString('th-TH')}</span>
                      </div>
                      <button 
                        onClick={async () => {
                          if (!window.confirm('ต้องการลบประกาศนี้ใช่หรือไม่?')) return;
                          try {
                            const res = await fetch(`${API_URL}/admin/announcements/${item._id}`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                            });
                            if (res.ok) {
                              showNotification('✓ ลบประกาศสำเร็จ', 'success');
                              loadAnnouncements();
                            } else {
                              showNotification('ลบประกาศไม่สำเร็จ', 'error');
                            }
                          } catch (err) {
                            showNotification('เกิดข้อผิดพลาด', 'error');
                          }
                        }}
                        className="bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white px-4 py-2 rounded-xl text-xs font-bold transition-colors border border-red-500/20 whitespace-nowrap"
                      >
                        ลบประกาศ
                      </button>
                    </div>
                  ))}
                  {announcements.length === 0 && (
                    <p className="text-center py-4 text-gray-500 text-sm">ยังไม่มีประกาศในระบบ</p>
                  )}
                </div>
              </div>

{/* 📌 ส่วนจัดการปักหมุดเกมฮิตในหน้าแอดมิน */}
            <div className="glass-panel p-8 rounded-3xl border-white/5 mt-8">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">⭐ จัดการเกมฮิตแนะนำ (Pin to Top)</h3>
              <p className="text-gray-400 text-sm mb-6">กดปุ่มเพื่อเลือกเกมที่ต้องการให้ขึ้นไปแสดงเป็นอันดับแรกสุดพร้อมป้ายแนะนำหน้าร้าน</p>
              
              <div className="overflow-x-auto max-h-96 overflow-y-auto custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-white/10 text-gray-400 text-sm">
                      <th className="pb-3 px-2 font-medium">ชื่อเกม / สินค้า</th>
                      <th className="pb-3 px-2 font-medium">หมวดหมู่</th>
                      <th className="pb-3 px-2 font-medium text-center">สถานะปัจจุบัน</th>
                      <th className="pb-3 px-2 font-medium text-center">จัดการปักหมุด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p._id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-2 font-bold text-white flex items-center gap-3">
                          {p.image && <img src={p.image} alt="" className="w-10 h-10 object-cover rounded-lg border border-white/10" />}
                          <span>{p.name}</span>
                        </td>
                        <td className="py-4 px-2 text-gray-400 text-sm">{p.category}</td>
                        <td className="py-4 px-2 text-center">
                          {p.isPinned ? (
                            <span className="bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 px-3 py-1 rounded-full text-xs font-bold">
                              ⭐ ปักหมุดอยู่
                            </span>
                          ) : (
                            <span className="text-gray-500 text-xs">ปกติ</span>
                          )}
                        </td>
                        <td className="py-4 px-2 text-center">
                          <button 
                            onClick={() => handleTogglePin(p._id, p.name)}
                            className={`smooth-btn px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                              p.isPinned 
                                ? 'bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500 hover:text-white' 
                                : 'bg-yellow-500 text-black hover:bg-yellow-400 shadow-lg'
                            }`}
                          >
                            {p.isPinned ? 'ยกเลิกปักหมุด' : '⭐ ปักหมุดเกมนี้'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

              {/* ตารางรายชื่อผู้ใช้งาน */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-white/10 text-gray-400 text-sm">
                      <th className="pb-3 px-2 font-medium">ชื่อผู้ใช้งาน (Tag)</th>
                      <th className="pb-3 px-2 font-medium">อีเมล</th>
                      <th className="pb-3 px-2 font-medium text-right">ยอดเงินปัจจุบัน</th>
                      <th className="pb-3 px-2 font-medium text-center">จัดการยอดเงิน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAdminUsers.map((u) => (
                      <tr key={u._id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-2 font-bold text-white">
                          {u.username} <span className="text-gray-500 font-normal">#{u.tag || '0000'}</span>
                          {u.isAdmin && <span className="ml-2 text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full">Admin</span>}
                        </td>
                        <td className="py-4 px-2 text-gray-400 text-sm">{u.email}</td>
                        <td className="py-4 px-2 text-right text-yellow-400 font-bold eng-num">฿{u.balance.toFixed(2)}</td>
                        <td className="py-4 px-2">
                          <div className="flex items-center justify-center gap-2">
                            <button 
                              onClick={() => handleUpdateBalance(u._id, u.username, u.tag || '0000', u.balance, 'subtract')}
                              className="bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white p-2 rounded-lg transition-colors border border-red-500/20"
                              title="หักเงิน"
                            >
                              <Minus size={16} />
                            </button>
                            <button 
                              onClick={() => handleUpdateBalance(u._id, u.username, u.tag || '0000', u.balance, 'add')}
                              className="bg-green-500/10 hover:bg-green-500 text-green-500 hover:text-white p-2 rounded-lg transition-colors border border-green-500/20"
                              title="เพิ่มเงิน"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredAdminUsers.length === 0 && (
                      <tr><td colSpan="4" className="text-center py-8 text-gray-500">ไม่พบรายชื่อผู้ใช้งานนี้</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="glass-panel p-8 rounded-3xl border-white/5">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2"><PlusCircle size={20}/> เติมคีย์สินค้า (Bulk Add)</h3>
              <div className="space-y-4">
                <select 
                  className="w-full bg-black/50 border border-white/10 p-3 rounded-xl text-white outline-none"
                  value={selectedAdminProduct}
                  onChange={(e) => setSelectedAdminProduct(e.target.value)}
                >
                  {products.filter(p => p.name.includes('CMD')).map(p => (
                    <option key={p._id} value={p.name}>{p.name}</option>
                  ))}
                </select>
                <textarea 
                  placeholder="ก๊อปคีย์มาวางที่นี่ บรรทัดละ 1 คีย์..."
                  className="w-full h-48 bg-black/50 border border-white/10 p-4 rounded-2xl text-white font-mono text-sm outline-none focus:border-yellow-500"
                  value={bulkKeys}
                  onChange={(e) => setBulkKeys(e.target.value)}
                ></textarea>
                <button 
                  onClick={async () => {
                    if(!bulkKeys.trim()) return showNotification("กรุณากรอกคีย์", "warning");
                    setLoading(true);
                    try {
                      const res = await fetch(`${API_URL}/admin/add-keys`, { 
                        method: 'POST', 
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }, 
                        body: JSON.stringify({ productName: selectedAdminProduct, keysString: bulkKeys }) 
                      });
                      const data = await res.json();
                      
                      if(res.ok) { 
                        showNotification("✓ เพิ่มคีย์เข้าสต็อกสำเร็จ!", "success"); 
                        setBulkKeys(""); 
                        loadProducts(); 
                        loadAdminData();
                      } else {
                        showNotification(`❌ ${data.message || 'เพิ่มคีย์ไม่สำเร็จ'}`, "error"); 
                      }
                    } catch (err) {
                      showNotification("❌ เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ", "error");
                    }
                    setLoading(false);
                  }} 
                  disabled={loading}
                  className="w-full py-4 bg-yellow-500 text-black font-black rounded-xl hover:bg-yellow-400 shadow-xl shadow-yellow-500/10 smooth-btn"
                >
                  {loading ? 'กำลังบันทึกข้อมูล...' : 'ยืนยันการเติมคีย์ลงฐานข้อมูล'}
                </button>
              </div>
            </div>
          </div>
        )}

      </main>

      {showBuyModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[100] flex items-center justify-center p-4 fade-in">
          <div className="glass-panel border border-yellow-500/40 rounded-3xl p-8 max-w-sm w-full shadow-[0_0_40px_rgba(212,175,55,0.15)]">
            <h3 className="text-2xl font-black mb-2 text-white">ยืนยันการสั่งซื้อ</h3>
            <div className="w-12 h-1 bg-yellow-500 rounded-full mb-6"></div>
            
            {/* 🔥 กล่องเตือน Denuvo ในหน้ายืนยัน */}
            {showBuyModal.denuvo && (
               <div className="mb-4 bg-red-500/10 border border-red-500/30 p-3 rounded-xl fade-in">
                 <p className="text-red-400 text-xs font-bold flex items-center gap-1.5 mb-1">
                   <AlertCircle size={14}/> คำเตือน: เกมนี้มีระบบป้องกัน Denuvo
                 </p>
                 <p className="text-gray-400 text-[10px] leading-relaxed">
                   ตัวเกมจำกัดการเข้าใช้งานครั้งแรก 5 เครื่อง/วัน หากคิวเต็มอาจจะต้องรอ 1-3 วันในการเข้าเกมครั้งแรก (เฉพาะตอนเข้าเกมครั้งแรกเท่านั้น)
                 </p>
               </div>
            )}

            <p className="text-gray-300 mb-8 leading-relaxed">
              คุณต้องการสั่งซื้อ <span className="text-yellow-400 font-bold bg-yellow-500/10 px-2 py-1 rounded-md">{showBuyModal.name}</span><br/>
              ในราคา <span className="text-yellow-400 font-black text-2xl eng-num pl-1">฿{showBuyModal.price}</span> ใช่หรือไม่?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowBuyModal(null)} className="smooth-btn flex-1 py-3.5 bg-black/40 border border-white/10 text-gray-300 font-bold rounded-xl hover:text-white">
                ยกเลิก
              </button>
              <button onClick={confirmPurchase} className="smooth-btn flex-1 py-3.5 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-xl shadow-[0_0_15px_rgba(212,175,55,0.2)]">
                ยืนยันซื้อสินค้า
              </button>
            </div>
          </div>
        </div>
      )}

{/* 🔥 หน้าต่างยืนยันการเช่าไอดี */}
      {showRentalModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[100] flex items-center justify-center p-4 fade-in">
          <div className="glass-panel border border-yellow-500/40 rounded-3xl p-8 max-w-sm w-full shadow-[0_0_40px_rgba(212,175,55,0.15)]">
            <h3 className="text-2xl font-black mb-2 text-white">เลือกระยะเวลาเช่า</h3>
            <p className="text-gray-400 text-sm mb-6">เกม: <span className="text-yellow-400 font-bold">{showRentalModal.name}</span></p>
            
            <div className="space-y-3 mb-8">
              {rentalRates.map((rate, idx) => (
                <label key={idx} className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${selectedRate.days === rate.days ? 'bg-yellow-500/20 border-yellow-500' : 'bg-black/40 border-white/10 hover:border-white/30'}`}>
                  <div className="flex items-center gap-3">
                    <input 
                      type="radio" 
                      name="rentalRate" 
                      checked={selectedRate.days === rate.days}
                      onChange={() => setSelectedRate(rate)}
                      className="accent-yellow-500 w-4 h-4"
                    />
                    <span className="text-white font-bold">เช่า {rate.days} วัน</span>
                  </div>
                  <span className="text-yellow-400 font-black eng-num">฿{rate.price}</span>
                </label>
              ))}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowRentalModal(null)} className="smooth-btn flex-1 py-3.5 bg-black/40 border border-white/10 text-gray-300 font-bold rounded-xl hover:text-white">
                ยกเลิก
              </button>
              <button onClick={confirmRentalPurchase} disabled={loading} className="smooth-btn flex-1 py-3.5 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-xl shadow-[0_0_15px_rgba(212,175,55,0.2)] disabled:opacity-50">
                {loading ? 'กำลังสั่งซื้อ...' : 'ยืนยันสั่งซื้อ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <div className="fixed bottom-6 right-6 z-[110] flex items-center gap-3 px-6 py-4 rounded-xl font-bold glass-panel fade-in border border-white/10 shadow-2xl">
          {notification.type === 'success' ? <CheckCircle size={20} className="text-green-400" /> : notification.type === 'warning' ? <AlertCircle size={20} className="text-yellow-400"/> : <AlertCircle size={20} className="text-red-400" />}
          <span className={notification.type === 'success' ? 'text-green-400' : notification.type === 'warning' ? 'text-yellow-400' : 'text-red-400'}>
            {notification.msg}
          </span>
        </div>
      )}

      {recentPurchase && (
        <div className="fixed bottom-6 left-6 z-[110] flex items-center gap-4 p-3 pr-6 rounded-2xl glass-panel border border-yellow-500/30 shadow-[0_0_20px_rgba(212,175,55,0.15)] fade-in max-w-sm">
          {recentPurchase.productImage ? (
            <img src={recentPurchase.productImage} alt="product" className="w-16 h-16 object-cover rounded-xl border border-white/10" />
          ) : (
            <div className="w-16 h-16 bg-black/40 rounded-xl flex items-center justify-center border border-white/10">
              <ShoppingBag className="text-yellow-500" size={24} />
            </div>
          )}
          
          <div className="flex flex-col">
            <span className="text-[10px] bg-green-500 text-black font-black px-2 py-0.5 rounded-md w-fit mb-1 animate-pulse">
              การสั่งซื้อล่าสุด
            </span>
            <p className="text-xs text-gray-400">ผู้ใช้: {recentPurchase.buyerName}</p>
            <p className="text-sm font-bold text-yellow-400 line-clamp-1">{recentPurchase.productName}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">{recentPurchase.time}</p>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[200] flex items-center justify-center p-4 fade-in">
          <div className="glass-panel border border-yellow-500/40 rounded-3xl p-6 md:p-8 max-w-2xl w-full shadow-[0_0_50px_rgba(212,175,55,0.2)] flex flex-col max-h-[90vh]">
            
            <div className="text-center mb-6">
              <h3 className="text-2xl font-black text-white mb-1">ข้อกำหนดและเงื่อนไขการให้บริการ</h3>
              <p className="text-yellow-500 text-xs tracking-widest font-poppins uppercase">SOCIETY×SHOP - TERMS OF SERVICE</p>
              <div className="w-16 h-1 bg-yellow-500 rounded-full mx-auto mt-3"></div>
            </div>

            <div className="overflow-y-auto space-y-4 pr-2 text-gray-300 text-sm leading-relaxed mb-6 custom-scrollbar bg-black/40 p-5 rounded-2xl border border-white/5">
              <div>
                <h4 className="text-white font-bold mb-1 flex items-center gap-2">⚠️ 1. นโยบายการห้ามจำหน่ายต่อ (No Resell)</h4>
                <p className="text-gray-400 text-xs">ห้ามมิให้นำผลิตภัณฑ์หรือบริการฟรีภายในร้านไปจำหน่ายต่อในทุกกรณี หากตรวจพบการละเมิด ทางร้านจะดำเนินการระงับบัญชีถาวรทันที</p>
              </div>

              <div>
                <h4 className="text-white font-bold mb-1 flex items-center gap-2">🛡️ 2. การยอมรับความเสี่ยง (Accept Risk)</h4>
                <p className="text-gray-400 text-xs">ผลิตภัณฑ์และซอฟต์แวร์ทั้งหมดภายในร้าน (ทั้งประเภทไม่มีค่าใช้จ่ายและมีค่าใช้จ่าย) อาจมีความเสี่ยงที่เกี่ยวข้องกับการใช้งาน ผู้ใช้ต้องยอมรับความเสี่ยงด้วยตนเองก่อนตัดสินใจใช้งาน</p>
              </div>

              <div>
                <h4 className="text-white font-bold mb-1 flex items-center gap-2">⚖️ 3. การปฏิเสธความรับผิดชอบกรณีถูกแบน (No Responsibility)</h4>
                <p className="text-gray-400 text-xs">ทางร้านจะไม่รับผิดชอบต่อความเสียหายใดๆ ทั้งสิ้น หากบัญชีเกมของท่านถูกระงับหรือถูกแบนจากการใช้งานทุกกรณี</p>
              </div>

              <div>
                <h4 className="text-white font-bold mb-1 flex items-center gap-2">🚫 4. กฎระเบียบภายในชุมชน (No Drama & Spam)</h4>
                <p className="text-gray-400 text-xs">ห้ามก่อความวุ่นวาย สร้างความเดือดร้อน สแปมข้อความ หรือแท็กผู้ดูแลระบบโดยไม่จำเป็นภายในช่องทาง Discord ของร้าน</p>
              </div>

              <div>
                <h4 className="text-white font-bold mb-1 flex items-center gap-2">🤝 5. มาตรการป้องกันการฉ้อโกง (Scam Protection)</h4>
                <p className="text-gray-400 text-xs">การทำธุรกรรมซื้อขายใดๆ ที่ไม่ผ่านระบบคนกลาง (Middleman) ของทางร้าน ทางร้านจะไม่รับผิดชอบต่อความเสียหายหากเกิดกรณีถูกฉ้อโกง</p>
              </div>

              <div>
                <h4 className="text-white font-bold mb-1 flex items-center gap-2">🔄 6. เงื่อนไขการรีเซ็ตคีย์ (Reset Key & Support)</h4>
                <p className="text-gray-400 text-xs">หากมีความประสงค์ต้องการรีเซ็ตคีย์ กรุณาเปิด Ticket ผ่านช่องทาง Discord เท่านั้น (อาจมีค่าธรรมเนียมตามประเภทของสินค้า)</p>
              </div>

              <div>
                <h4 className="text-white font-bold mb-1 flex items-center gap-2">🕒 7. เวลาทำการและแหล่งที่มาของบริการ (Service Time & Provider)</h4>
                <p className="text-gray-400 text-xs">ร้านเปิดให้บริการเวลา 07:00 น. - 21:00 น. สินค้าบางรายการเป็นบริการที่เราจัดหาจากผู้ให้บริการภายนอก ซึ่งบางกรณีอาจไม่สามารถแก้ไขโค้ดต้นฉบับได้โดยตรง</p>
              </div>
            </div>

            <button 
              onClick={handleAcceptTerms}
              className="smooth-btn w-full py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold text-base rounded-xl shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:brightness-110"
            >
              ข้าพเจ้าได้อ่านและยอมรับเงื่อนไขการให้บริการทั้งหมด
            </button>

          </div>
        </div>
      )}
    </div> 
  ); 
}